import React, { useCallback, useLayoutEffect, useState, useRef, useContext, useEffect, useMemo } from "react";
import { FlexLeftCol, FlexLeftColNoGap, GoalStyles } from "styles/global-style";
import { AuthContext } from "utils/AuthContext";
import useKey from "utils/KeyListener";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import Button from "components/Button";
import Story from "components/Work/Story";
import Loading from "pages/Loading";
import Search from "components/Search";
import Modal from "components/Modal";
import Input from "components/Input";
import EditTask from "components/Work/EditTask";
import FilterButton from "components/FilterButton";
import { FaChevronDown, FaChevronRight, FaTimes } from "react-icons/fa";

import arrow from "imgs/arrow.png";
import ButtonTiny from "components/ButtonTiny";
import plus from "imgs/plus.png";
import { getDefaultColumns, getDefaultColumnOrder } from "../../utils/DefaultColumns";

const clone = require("rfdc")();

export default function Work() {
  const { user, supabase, refresh } = useContext(AuthContext);

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStory, setEditingStory] = useState();
  const [editingStoryModal, setEditingStoryModal] = useState(null); // For modal editing (vs inline editing)
  const [editingTask, setEditingTask] = useState(null); // {story: {id, index}, col: {id, index}} or {task: {id}}
  const [subEpics, setSubEpics] = useState([]); // Sub-epics for task assignment
  const [goalsHidden, setGoalsHidden] = useState([]);
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [selectedStory, setSelectedStory] = useState(-1);
  const [selectedTask, setSelectedTask] = useState(-1);
  const [collapsedGoals, setCollapsedGoals] = useState({});
  const [collapsedStories, setCollapsedStories] = useState({});
  const [hideLetsGo, setHideLetsGo] = useState(false);
  const [showCompletedStories, setShowCompletedStories] = useState(false);
  const [columns, setColumns] = useState([
    { id: "TODO", title: "TODO", order: 0 },
    { id: "IN_PROGRESS", title: "IN PROGRESS", order: 1 },
    { id: "REVIEW", title: "REVIEW", order: 2 },
    { id: "DONE", title: "DONE", order: 3 }
  ]);
  // eslint-disable-next-line no-unused-vars
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list' - setViewMode unused but viewMode is used
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const userChangingViewMode = useRef(false);
  const isOptimisticUpdate = useRef(false);
  const userChangingCompletedToggle = useRef(false);

  const newTitle = useRef();
  const users = useRef();
  const [usersForDisplay, setUsersForDisplay] = useState([]); // Assignable users + deleted users who still have work assigned (for showing names)
  const storyRefs = useRef({}); // Refs to track story elements for scrolling

  // Memoized helper to get flattened story indices across all goals
  // Only includes visible stories (filters out completed ones when showCompletedStories is false)
  const flatStories = useMemo(() => {
    return goals.flatMap((g, gi) => {
      const storyOrder = g.workitems_order || [];
      return storyOrder
        .map((storyId, si) => {
          const story = g.stories.find((st) => st.id === storyId);
          // Include story if completed stories are shown, or if story is not completed
          if (story && (showCompletedStories || story.status !== "COMPLETE")) {
            return { gi, si };
          }
          return null;
        })
        .filter(item => item !== null);
    });
  }, [goals, showCompletedStories]);

  //----------------------------------------------------

  const fetchData = useCallback(async () => {
    // Skip fetch if we're doing an optimistic update (to prevent overwriting it)
    if (isOptimisticUpdate.current) {
      return;
    }
    
    // Only fetch users, not goals automatically (exclude deleted – they can't be assigned but may still appear as assignees)
    const { data: _users, error: usersError } = await supabase
      .from("memberships")
      .select("*, people(*, days(*), images(*))")
      .eq("org_id", user.org.id)
      .neq("status", "deleted")
      .order("firstname", { foreignTable: "people", ascending: true });
    
    if (usersError) {
      console.error("Error fetching users with images:", usersError);
      // Fallback to simple query without images
      const { data: _usersFallback } = await supabase
        .from("memberships")
        .select("*, people(*, days(*))")
        .eq("org_id", user.org.id)
        .neq("status", "deleted")
        .order("firstname", { foreignTable: "people", ascending: true });
      
      users.current = _usersFallback.map((person) => ({ 
        id: person.people.id, 
        firstname: person.people.firstname, 
        lastname: person.people.lastname 
      }));
    } else {
    
    // Process users with profile images
    const processedUsers = await Promise.all(
      _users.map(async (person) => {
        let avatar = null;
        if (person.people.images) {
          // Handle both array and single object cases
          const image = Array.isArray(person.people.images) ? person.people.images[0] : person.people.images;
          if (image && image.url) {
            try {
              const { data: signedUrl } = await supabase.storage
                .from('avatars')
                .createSignedUrl(image.url, 3600); // 1 hour expiry
              avatar = signedUrl?.signedUrl || null;
            } catch (error) {
              console.warn("Failed to create signed URL for avatar:", error);
              // Continue without avatar URL rather than failing
            }
          }
        }
        
        return { 
          id: person.people.id, 
          firstname: person.people.firstname, 
          lastname: person.people.lastname,
          avatar: avatar
        };
      })
    );
    
      users.current = processedUsers;
    }

    // If user has no "joins" or "leaves" days in project, do not allow project access
    setHasProjectAccess(_users.some((m) => m.people.id === user.id && m.people.days.some((day) => day.system_type === "joins" || day.system_type === "leaves")));

    // Load custom columns from settings or use defaults
    let customColumns = null;
    const defaultColumns = [
      { id: "TODO", title: "TODO", order: 0 },
      { id: "IN_PROGRESS", title: "IN PROGRESS", order: 1 },
      { id: "REVIEW", title: "REVIEW", order: 2 },
      { id: "DONE", title: "DONE", order: 3 }
    ];
    let columnsToUse = defaultColumns;
    
    if (supabase && user?.proj?.id) {
      const { data: columnsSetting } = await supabase
        .from("settings")
        .select("value")
        .eq("project_id", user.proj.id)
        .eq("name", "work_columns")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (columnsSetting?.value) {
        try {
          customColumns = JSON.parse(columnsSetting.value);
          columnsToUse = customColumns;
        } catch (e) {
          console.error("Failed to parse work_columns setting:", e);
        }
      }
      
      setColumns(columnsToUse);
    }

    // Load work items from unified work table
    if (supabase && user?.proj?.id) {
      // First, ensure unplanned goal exists
      let { data: unplannedGoal } = await supabase
        .from("goals")
        .select("*")
        .eq("project_id", user.proj.id)
        .eq("type", "unplanned")
        .maybeSingle();

      if (!unplannedGoal) {
        // Create unplanned goal if it doesn't exist
        const { data: newUnplannedGoal } = await supabase
          .from("goals")
          .insert({
            name: "Backlog",
            type: "unplanned",
            project_id: user.proj.id,
          })
          .select()
          .single();
        unplannedGoal = newUnplannedGoal;
      }

      // Fetch all work items for this project
      const { data: workItems, error: workError } = await supabase
        .from("work")
        .select("*")
        .eq("project_id", user.proj.id)
        .order("created_at", { ascending: true });

      if (workError) {
        console.error("Error fetching work items:", workError);
        setGoals([{ ...unplannedGoal, stories: [] }]);
      } else {
        // Build tree structure from flat work items
        // Stories are items with no parent (or type=epic/subepic)
        // Tasks are items with a parent
        const storiesMap = new Map();
        const allItems = workItems || [];

        // Include deleted users who still have work assigned so assignee names display
        const assignableUsers = users.current || [];
        const ownerIds = [...new Set(allItems.map((i) => i.owner_id).filter(Boolean))];
        const missingOwnerIds = ownerIds.filter((id) => !assignableUsers.some((u) => u.id === id));
        if (missingOwnerIds.length > 0) {
          const { data: missingPeople } = await supabase
            .from("people")
            .select("id, firstname, lastname")
            .in("id", missingOwnerIds);
          const deletedForDisplay = (missingPeople || []).map((p) => ({
            id: p.id,
            firstname: p.firstname || "",
            lastname: p.lastname || "",
            avatar: null,
          }));
          setUsersForDisplay([...assignableUsers, ...deletedForDisplay]);
        } else {
          setUsersForDisplay(assignableUsers);
        }
        
        // Use columns loaded from settings or defaults
        
        // First pass: create map of all items
        allItems.forEach(item => {
          const itemData = {
            id: item.id,
            title: item.title,
            description: item.description,
            estimate: item.estimate,
            owner_id: item.owner_id,
            status: item.status,
            type: item.type,
            goal_id: item.goal_id,
            parent_id: item.parent_id,
            child_order: item.child_order || [],
            children: []
          };
          storiesMap.set(item.id, itemData);
        });

        // Second pass: build tree structure and attach children
        const itemsByGoal = new Map();
        
        allItems.forEach(item => {
          const itemData = storiesMap.get(item.id);
          // Stories are standalone (parent_id = null) or are epics/subepics
          const isStory = item.type === 'story' || item.type === 'epic' || item.type === 'subepic' || (!item.parent_id && item.type !== 'task');
          
          if (isStory) {
            // This is a story (standalone, parent_id = null) or epic/subepic
            if (item.goal_id) {
              if (!itemsByGoal.has(item.goal_id)) {
                itemsByGoal.set(item.goal_id, []);
              }
              itemsByGoal.get(item.goal_id).push(itemData);
            }
          } else if (item.type === 'task') {
            // This is a task - attach to story via story_id, or to parent via parent_id
            if (item.story_id) {
              // Task belongs to a story
              const story = storiesMap.get(item.story_id);
              if (story) {
                if (!story.children) story.children = [];
                story.children.push(itemData);
              }
            } else if (item.parent_id) {
              // Task belongs to a parent (epic/subepic)
              const parent = storiesMap.get(item.parent_id);
              if (parent) {
                if (!parent.children) parent.children = [];
                parent.children.push(itemData);
              }
            }
          }
        });

        // Third pass: sort children by child_order for each story
        itemsByGoal.forEach((items, goalId) => {
          items.forEach(story => {
            if (story.children && story.child_order && story.child_order.length > 0) {
              const orderedChildren = story.child_order
                .map(childId => story.children.find(c => c.id === childId))
                .filter(Boolean);
              const unorderedChildren = story.children.filter(c => !story.child_order.includes(c.id));
              story.children = [...orderedChildren, ...unorderedChildren];
            }
          });
        });

        // Organize by child_order for each goal
        const goalsWithWork = [];
        
        // Get unplanned goal's work items
        const unplannedWork = itemsByGoal.get(unplannedGoal.id) || [];
        
        // First, filter based on toggle: when off, exclude completed stories
        // When on, include ALL stories (including completed ones)
        const filteredWork = showCompletedStories 
          ? unplannedWork  // Show all stories when toggle is on
          : unplannedWork.filter(item => {
              // Filter out completed stories when toggle is off
              // Check status field - if it's "COMPLETE", exclude it
              const status = item?.status;
              return status !== "COMPLETE";
            });
        
        // Then, order stories: first by workitems_order, then append any not in order
        let orderedUnplannedWork = [];
        if (unplannedGoal.workitems_order && unplannedGoal.workitems_order.length > 0) {
          const orderedIds = unplannedGoal.workitems_order;
          const orderedIdsSet = new Set(orderedIds);
          
          // Get stories that are in workitems_order (from filtered list)
          const orderedStories = orderedIds
            .map(id => filteredWork.find(item => item.id === id))
            .filter(Boolean);
          
          // Get stories not in workitems_order (from filtered list)
          const unorderedStories = filteredWork.filter(item => !orderedIdsSet.has(item.id));
          
          // Combine: ordered stories first, then unordered
          orderedUnplannedWork = [...orderedStories, ...unorderedStories];
        } else {
          // No order: just use filtered list, sorted by id
          orderedUnplannedWork = filteredWork.sort((a, b) => a.id - b.id);
        }

        // Build columns structure for Kanban view
        goalsWithWork.push({
          ...unplannedGoal,
          stories: orderedUnplannedWork.map(story => {
            // Group tasks by status (column)
            const tasksByStatus = new Map();
            (story.children || []).forEach(task => {
              const status = task.status || 'TODO';
              if (!tasksByStatus.has(status)) {
                tasksByStatus.set(status, []);
              }
              tasksByStatus.get(status).push(task);
            });
            
            // Create column structure matching the column definitions
            const storyColumns = columnsToUse.map(col => ({
              id: col.id,
              title: col.title,
              tasks: tasksByStatus.get(col.id) || [],
              tasks_order: (tasksByStatus.get(col.id) || []).map(t => t.id)
            }));
            
            return {
              ...story,
              columns: storyColumns
            };
          })
        });

        setGoals(goalsWithWork);
      }
    }

    setGoalsHidden(user.proj.settings.find((s) => s.name === "goalsHidden") || []);
    setLoading(false);
  }, [supabase, user, showCompletedStories]);


  const removeGoalFromView = useCallback(async (goalId) => {
    // Don't allow removing the unplanned goal
    const goal = goals.find(g => g.id === goalId);
    if (goal && goal.type === "unplanned") {
      return;
    }
    setGoals(prev => prev.filter(g => g.id !== goalId));
  }, [goals]);

  // eslint-disable-next-line no-unused-vars
  const submitStory = useCallback(async () => {
    const storyToEdit = editingStoryModal || editingStory;
    if (!storyToEdit) return;
    
    if (newTitle.current?.value.trim()) {
      if (storyToEdit.title) {
        // Editing a story
        await supabase.from("work").update({ title: newTitle.current.value.trim() }).eq("id", storyToEdit.id);
        
        // Update local state
        setGoals(prev => prev.map(goal => 
          goal.id === storyToEdit.goal.id 
            ? {
                ...goal,
                stories: goal.stories.map(story => 
                  story.id === storyToEdit.id 
                    ? { ...story, title: newTitle.current.value.trim() }
                    : story
                )
              }
            : goal
        ));
      } else {
        // Adding a new story
        const res = await supabase.from("stories").insert({ title: newTitle.current.value.trim(), goal: storyToEdit.goal.id }).select("id");
        const newStoryId = res.data[0].id;
        
        // Create default columns for the new story
        const defaultColumns = getDefaultColumns(newStoryId);
        const { data: createdColumns } = await supabase
          .from("columns")
          .insert(defaultColumns)
          .select();

        if (createdColumns) {
          // Update the story with columns_order
          await supabase
            .from("stories")
            .update({ columns_order: getDefaultColumnOrder(createdColumns) })
            .eq("id", newStoryId);

          const goalIdx = goals.findIndex((g) => g.id === storyToEdit.goal.id);
          let newOrder;
          
          // If insertAfter is specified, insert after that story
          if (storyToEdit.insertAfter) {
            const currentOrder = goals[goalIdx].workitems_order || [];
            const insertIndex = currentOrder.indexOf(storyToEdit.insertAfter);
            if (insertIndex >= 0) {
              newOrder = [...currentOrder];
              newOrder.splice(insertIndex + 1, 0, newStoryId);
            } else {
              newOrder = currentOrder.concat(newStoryId);
            }
          } else {
            newOrder = goals[goalIdx].workitems_order?.concat(newStoryId) || [newStoryId];
          }
          
          await supabase.from("goals").update({ workitems_order: newOrder }).eq("id", storyToEdit.goal.id);
          
          // Update local state with the complete story including columns
          setGoals(prev => prev.map(goal => 
            goal.id === storyToEdit.goal.id 
              ? {
                  ...goal,
                  workitems_order: newOrder,
                  stories: storyToEdit.insertAfter
                    ? goal.stories.reduce((acc, story) => {
                        acc.push(story);
                        if (story.id === storyToEdit.insertAfter) {
                          acc.push({ 
                            id: newStoryId, 
                            title: newTitle.current.value.trim(), 
                            columns_order: getDefaultColumnOrder(createdColumns),
                            columns: createdColumns.map(col => ({
                              ...col,
                              tasks: [],
                            }))
                          });
                        }
                        return acc;
                      }, [])
                    : [...goal.stories, { 
                    id: newStoryId, 
                    title: newTitle.current.value.trim(), 
                    columns_order: getDefaultColumnOrder(createdColumns),
                    columns: createdColumns.map(col => ({
                      ...col,
                      tasks: [],
                    }))
                  }]
                }
              : goal
          ));
        }
      }
      setEditingStory(false);
      setEditingStoryModal(null);
    }
  }, [goals, editingStory, editingStoryModal, supabase]);

  // eslint-disable-next-line no-unused-vars
  const submitTask = useCallback(
    async (task, keepOpen) => {
      let newTaskId;
      if (editingTask?.task?.id > -1) {
        // Editing an existing task
        // Calculate the actual owner_id value to use (handle null explicitly for removal)
        const actualOwnerId = task.person_id !== undefined 
          ? (task.person_id === "remove" ? null : task.person_id)
          : (task.owner_id !== undefined ? task.owner_id : editingTask.task.owner_id);
        
        const updateData = { 
          title: task.title, 
          owner_id: actualOwnerId, 
          description: task.description, 
          estimate: task.estimate 
        };
        // Include parent_id if it's being updated
        if (task.parent_id !== undefined) {
          updateData.parent_id = task.parent_id;
        }
        await supabase.from("work").update(updateData).eq("id", editingTask.task.id);
        
        setEditingTask({ ...editingTask, task: { ...editingTask.task, ...task, owner_id: actualOwnerId } });
        
        // Update local state for task editing
        setGoals(prev => prev.map(goal => 
          goal.id === editingTask.goal.id 
            ? {
                ...goal,
                stories: goal.stories.map(story => 
                  story.id === editingTask.story.id 
                    ? {
                        ...story,
                        children: story.children.map(t => 
                                  t.id === editingTask.task.id 
                            ? { ...t, title: task.title, owner_id: actualOwnerId, description: task.description, estimate: task.estimate, parent_id: task.parent_id !== undefined ? task.parent_id : t.parent_id }
                                    : t
                        )
                      }
                    : story
                )
              }
            : goal
        ));
      } else {
        // Adding a new task
        const goalIdx = goals.findIndex((g) => g.id === editingTask.goal.id);
        const storyIdx = goals[goalIdx].stories.findIndex((s) => s.id === editingTask.story.id);
        const colIdx = goals[goalIdx].stories[storyIdx].columns.findIndex((c) => c.id === editingTask.col.id);

        const res = await supabase.from("tasks").insert({ title: task.title, column_id: editingTask.col.id, person_id: task.person_id }).select("id");
        newTaskId = res.data[0].id;
        const newOrder = goals[goalIdx].stories[storyIdx].columns[colIdx].tasks_order?.concat(newTaskId) || [newTaskId];
        await supabase.from("columns").update({ tasks_order: newOrder }).eq("id", editingTask.col.id);
        
        // Update local state for new task
        setGoals(prev => prev.map(goal => 
          goal.id === editingTask.goal.id 
            ? {
                ...goal,
                stories: goal.stories.map(story => 
                  story.id === editingTask.story.id 
                    ? {
                        ...story,
                        columns: story.columns.map(column => 
                          column.id === editingTask.col.id 
                            ? {
                                ...column,
                                tasks_order: newOrder,
                                tasks: [...column.tasks, { id: newTaskId, title: task.title, person_id: task.person_id }]
                              }
                            : column
                        )
                      }
                    : story
                )
              }
            : goal
        ));
      }

      if (!keepOpen) {
        setEditingTask(null);
      } else if (newTaskId) setEditingTask({ ...editingTask, task: { id: newTaskId } });
    },
    [supabase, goals, editingTask]);

  // Pre-fetch sub-epics when component mounts or project changes
  useEffect(() => {
    async function fetchSubEpics() {
      if (!supabase || !user?.proj?.id) return;
      
      const { data: subEpicsData } = await supabase
        .from("work")
        .select("id, title")
        .eq("project_id", user.proj.id)
        .eq("type", "subepic")
        .order("title", { ascending: true });
      
      setSubEpics(subEpicsData || []);
    }
    
    fetchSubEpics();
  }, [supabase, user?.proj?.id]);

  // Handle sub-epic selection
  const handleSubEpicSelect = useCallback(
    async (subEpicId) => {
      if (!editingTask?.task?.id || editingTask.task.id <= -1) return;
      
      const taskId = editingTask.task.id;
      const oldParentId = editingTask.task.parent_id;
      
      // Update task's parent_id
      await supabase
        .from("work")
        .update({ parent_id: subEpicId })
        .eq("id", taskId);
      
      // Update old subepic's child_order if task was previously assigned to a subepic
      if (oldParentId) {
        const { data: oldSubepic } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", oldParentId)
          .eq("type", "subepic")
          .single();
        
        if (oldSubepic && oldSubepic.child_order) {
          const newChildOrder = oldSubepic.child_order.filter(id => id !== taskId);
          await supabase
            .from("work")
            .update({ child_order: newChildOrder })
            .eq("id", oldParentId);
        }
      }
      
      // Update new subepic's child_order if assigning to a subepic
      if (subEpicId) {
        const { data: newSubepic } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", subEpicId)
          .eq("type", "subepic")
          .single();
        
        if (newSubepic) {
          const currentChildOrder = newSubepic.child_order || [];
          // Only add if not already in the array
          if (!currentChildOrder.includes(taskId)) {
            const newChildOrder = [...currentChildOrder, taskId];
            await supabase
              .from("work")
              .update({ child_order: newChildOrder })
              .eq("id", subEpicId);
          }
        }
      }
      
      // Update local state
      setEditingTask(prev => ({
        ...prev,
        task: { ...prev.task, parent_id: subEpicId }
      }));
      
      // Update goals state
      setGoals(prev => prev.map(goal => ({
        ...goal,
        stories: goal.stories.map(story => ({
          ...story,
          children: story.children.map(t => 
            t.id === editingTask.task.id 
              ? { ...t, parent_id: subEpicId }
              : t
          )
        }))
      })));
    },
    [supabase, editingTask]
  );

  const deleteTask = useCallback(
    async (id) => {
      if (window.confirm("Are you sure you want to delete this task?")) {
        let story;
        let storyId;
        let newSelectedTask = -1;

        if (editingTask) {
          // Deleted from inside EditTask
          storyId = editingTask.story.id;
          const goal = goals.find((g) => g.id === editingTask.goal.id);
          if (goal) {
            story = goal.stories.find((s) => s.id === storyId);
          }
        } else {
          // If deleted from open Work view - use same pattern as navigation
          const flatS = flatStories;
          
          if (selectedStory >= 0 && selectedStory < flatS.length) {
            const { gi, si } = flatS[selectedStory];
            const goal = goals[gi];
            const storyOrder = goal.workitems_order || [];
            const storyIdFromOrder = storyOrder[si];
            story = goal.stories.find((st) => st.id === storyIdFromOrder);
            storyId = story?.id;
            
            // Calculate new selection before deletion
            if (story && story.children && selectedTask > -1) {
              // Build array of all tasks across all columns (same pattern as navigation)
              const allTasks = [];
              columns.forEach((col, colIdx) => {
                const colTasks = story.children.filter(task => task.status === col.id);
                colTasks.forEach((task, taskIdx) => {
                  allTasks.push({ colIdx, taskIdx, task });
                });
              });
              
              // Find the current task index
              if (selectedTask >= 0 && selectedTask < allTasks.length) {
                const currentTask = allTasks[selectedTask];
                if (currentTask.task.id === id) {
                  // We're deleting the currently selected task
                  // Try to select the next task, or previous if none
                  if (selectedTask < allTasks.length - 1) {
                    // Select next task (will be at the same index after deletion)
                    newSelectedTask = selectedTask;
                  } else if (selectedTask > 0) {
                    // Select previous task (will be at index - 1 after deletion)
                    newSelectedTask = selectedTask - 1;
                  } else {
                    // No tasks remain after deletion, select story
                    newSelectedTask = -1;
                  }
                }
              }
            }
          }
        }

        if (story && storyId) {
          // Update parent story's child_order to remove this task
          const currentChildOrder = story.child_order || [];
          const newChildOrder = currentChildOrder.filter(taskId => taskId !== id);
          
          // Update story's child_order in database
          await supabase
            .from("work")
            .update({ child_order: newChildOrder.length > 0 ? newChildOrder : null })
            .eq("id", storyId);
          
          // Delete the task (work item) from database
          await supabase
            .from("work")
            .delete()
            .eq("id", id);
          
          setEditingTask(null);
          setSelectedTask(newSelectedTask);
          fetchData();
        }
      }
    },
    [fetchData, supabase, goals, editingTask, selectedStory, selectedTask, columns, flatStories]
  );

  const completeStory = useCallback(
    async (indexPath, id) => {
      try {
        // Update story's status to COMPLETE
        const { error: storyError } = await supabase
          .from("work")
          .update({ status: "COMPLETE" })
          .eq("id", id);
        
        if (storyError) {
          console.error("Error completing story:", storyError);
          throw storyError;
        }
        
        // Clear selection and refresh data
        setSelectedStory(-1);
        setSelectedTask(-1);
        setEditingTask(null);
        
        fetchData();
        
      } catch (error) {
        console.error("Error completing story:", error);
      }
    },
    [fetchData, supabase]
  );

  const reactivateStory = useCallback(
    async (indexPath, id) => {
      try {
        // Update story's status to null (remove COMPLETE status)
        const { error: storyError } = await supabase
          .from("work")
          .update({ status: null })
          .eq("id", id);
        
        if (storyError) {
          console.error("Error reactivating story:", storyError);
          throw storyError;
        }
        
        // Clear selection and refresh data
        setSelectedStory(-1);
        setSelectedTask(-1);
        setEditingTask(null);
        
        fetchData();
        
      } catch (error) {
        console.error("Error reactivating story:", error);
      }
    },
    [fetchData, supabase]
  );

  const toggleCompletedStories = useCallback(async () => {
    const newValue = !showCompletedStories;
    
    // Set flag to prevent useEffect from overriding
    userChangingCompletedToggle.current = true;
    
    // Update state immediately
    setShowCompletedStories(newValue);
    
    // Save or delete the setting based on the new value
    if (!newValue) {
      // Delete the setting when toggle is off (default state)
      if (supabase && user?.proj?.id && user?.id) {
        const { error } = await supabase
          .from('settings')
          .delete()
          .eq('name', 'workShowCompletedStories')
          .eq('project_id', user.proj.id)
          .eq('user_id', user.id);
        if (error) console.error('Error deleting showCompletedStories setting:', error);
        else {
          // Refresh user data to update cached settings
          refresh();
        }
      }
    } else {
      // Save the setting when toggle is on
      if (supabase && user?.proj?.id && user?.id) {
        // Check if setting already exists for this user/project
        const { data: existingSetting } = await supabase
          .from('settings')
          .select('id')
          .eq('name', 'workShowCompletedStories')
          .eq('project_id', user.proj.id)
          .eq('user_id', user.id);
        let error;
        if (existingSetting && existingSetting.length > 0) {
          // Update existing setting
          const result = await supabase
            .from('settings')
            .update({ value: 'true' })
            .eq('id', existingSetting[0].id);
          error = result.error;
        } else {
          // Insert new setting
          const result = await supabase.from('settings').insert({
            name: 'workShowCompletedStories',
            value: 'true',
            project_id: user.proj.id,
            user_id: user.id,
          });
          error = result.error;
        }
        
        if (error) console.error('Error saving showCompletedStories setting:', error);
        else {
          // Refresh user data to update cached settings
          refresh();
        }
      }
    }
    
    // Reset flag after a short delay to allow useEffect to run again
    setTimeout(() => {
      userChangingCompletedToggle.current = false;
    }, 100);
  }, [showCompletedStories, supabase, user?.proj?.id, refresh, user?.id]);

  const deleteStory = useCallback(
    async (indexPath, id, title, skipConfirmation = false) => {
      if (skipConfirmation || window.confirm("Delete story: " + title + "\n\n Are you sure?")) {
        try {
          const [gi] = indexPath;
          const goal = goals[gi];
          
          if (!goal) {
            console.error("Goal not found");
            return;
          }
          
          // Delete all child tasks (work items with story_id = storyId and type = 'task')
          const { error: tasksError } = await supabase
            .from("work")
            .delete()
            .eq("story_id", id)
            .eq("type", "task");
          
          if (tasksError) {
            console.error("Error deleting tasks:", tasksError);
            throw tasksError;
          }
          
          // Update goal's workitems_order to remove the story ID
          const orderField = "workitems_order";
          const currentOrder = goal[orderField] || [];
          const newOrder = currentOrder.filter((val) => val !== id);
          
          const { error: goalError } = await supabase
            .from("goals")
            .update({ [orderField]: newOrder.length > 0 ? newOrder : null })
            .eq("id", goal.id);
          
          if (goalError) {
            console.error("Error updating goal:", goalError);
            throw goalError;
          }
          
          // Delete the story (work item) itself
          const { error: storyError } = await supabase
            .from("work")
            .delete()
            .eq("id", id);
          
          if (storyError) {
            console.error("Error deleting story:", storyError);
            throw storyError;
          }
          
          // Clear selection and refresh data
          setSelectedStory(-1);
          setSelectedTask(-1);
          setEditingTask(null);
          
          fetchData();
          
        } catch (error) {
          console.error("Error deleting story:", error);
        }
      }
    },
    [goals, fetchData, supabase]
  );

  // eslint-disable-next-line no-unused-vars
  const validateText = useCallback(() => {
    // setInputValid removed - function kept for potential future use
  }, []);

  const draggedStory = useCallback(
    async (sourcePath, destPath, res) => {
      const { destination, source } = res;
      let _goals = clone(goals);

      // Remove story from source goal's workitems_order
      const sourceOrder = _goals[sourcePath[0]].workitems_order || [];
      const storyId = sourceOrder.splice(source.index, 1)[0];
      const story = _goals[sourcePath[0]].stories.splice(
        _goals[sourcePath[0]].stories.findIndex((s) => s.id === storyId),
        1
      )[0];

      // If no remaining stories, set storage to null
      if (sourceOrder.length < 1) {
        _goals[sourcePath[0]].workitems_order = null;
      } else {
        _goals[sourcePath[0]].workitems_order = sourceOrder;
      }
      if (_goals[sourcePath[0]].stories && _goals[sourcePath[0]].stories.length < 1) _goals[sourcePath[0]].stories = null;

      // Add story to destination position
      const destOrder = _goals[destPath[0]].workitems_order || [];
      if (destOrder.length > 0) destOrder.splice(destination.index, 0, storyId);
      else {
        if (!_goals[destPath[0]].workitems_order) _goals[destPath[0]].workitems_order = [storyId];
        else _goals[destPath[0]].workitems_order.push(storyId);
      }
      if (_goals[destPath[0]].stories?.length > 0) _goals[destPath[0]].stories.push(story);
      else _goals[destPath[0]].stories = [story];

      setGoals(_goals);
      const updateOrder = (order) => order && order.length > 0 ? order : null;
      
      if (_goals[sourcePath[0]].id !== _goals[destPath[0]].id)
        await supabase.from("goals").update({ workitems_order: updateOrder(_goals[sourcePath[0]].workitems_order) }).eq("id", _goals[sourcePath[0]].id);
      await supabase.from("goals").update({ workitems_order: updateOrder(_goals[destPath[0]].workitems_order) }).eq("id", _goals[destPath[0]].id);
      
      // Update goal_id in work table
      if (_goals[sourcePath[0]].id !== _goals[destPath[0]].id) {
        await supabase.from("work").update({ goal_id: _goals[destPath[0]].id }).eq("id", storyId);
      }

      fetchData();
    },
    [fetchData, goals, supabase]
  );

  const moveDraggable = useCallback(
    async (sourcePath, destPath, res) => {
      if (res.type === "story") {
        draggedStory(sourcePath, destPath, res);
        return;
      }

      // For tasks: sourcePath/destPath are [goalIdx, storyIdx, columnIdx, taskIdx]
      const sourceGoal = goals[sourcePath[0]];
      const sourceStoryId = sourceGoal.workitems_order?.[sourcePath[1]];
      const sourceStoryIdx = sourceGoal.stories.findIndex((s) => s.id === sourceStoryId);
      const destGoal = goals[destPath[0]];
      const destStoryId = destGoal.workitems_order?.[destPath[1]];
      const destStoryIdx = destGoal.stories.findIndex((s) => s.id === destStoryId);

      const sourceStory = sourceGoal.stories[sourceStoryIdx];
      const destStory = destGoal.stories[destStoryIdx];
      
      // Columns are now virtual - based on status field
      const sourceCol = columns[sourcePath[2]]; // Column from settings
      const destCol = columns[destPath[2]]; // Column from settings
      
      // Find the task from source column by filtering tasks by status
      const sourceTasks = sourceStory.children?.filter(t => t.status === sourceCol.id) || [];
      const taskId = sourceTasks[sourcePath[3]]?.id;
      if (!taskId) return;
      
      const task = sourceStory.children?.find(t => t.id === taskId);
      if (!task) return;

      // Optimistic update: Update local state immediately
      isOptimisticUpdate.current = true;
      const updatedTask = { ...task };
      if (sourceCol.id !== destCol.id) {
        updatedTask.status = destCol.id;
      }

      let updatedGoals = clone(goals);

      // If moving to a different story, update story_id and both stories' child_order
      if (sourceStory.id !== destStory.id) {
        // Update task's story_id locally (keep parent_id as is - null for Work page tasks, or sub-epic for Project page tasks)
        updatedTask.story_id = destStory.id;
        
        // Remove task from source story's children and child_order
        const sourceGoalIdx = sourcePath[0];
        const sourceGoalUpdated = updatedGoals[sourceGoalIdx];
        // Verify we found the right story
        const sourceStoryInGoal = sourceGoalUpdated.stories[sourceStoryIdx];
        if (!sourceStoryInGoal || sourceStoryInGoal.id !== sourceStory.id) {
          console.error("Source story mismatch in optimistic update");
          return;
        }
        
        // Ensure task is removed
        const sourceChildOrder = sourceStoryInGoal.child_order || [];
        const newSourceChildOrder = sourceChildOrder.filter(id => id !== taskId);
        
        // Verify the task was actually in the source
        const taskWasInSource = sourceStoryInGoal.children?.some(t => t && t.id === taskId);
        if (!taskWasInSource) {
          console.warn("Task was not found in source story's children during move");
        }
        
        // Add task to destination story's children and child_order
        const destGoalIdx = destPath[0];
        const destStoryIdxInGoal = destStoryIdx;
        const destGoal = updatedGoals[destGoalIdx];
        const destStoryInGoal = destGoal.stories[destStoryIdxInGoal];
        
        const destChildOrder = destStoryInGoal.child_order || [];
        
        // Calculate position based on destination column and index
        const tasksByStatus = new Map();
        (destStoryInGoal.children || []).forEach(t => {
          if (t.id !== taskId) {
            const status = t.status || 'TODO';
            if (!tasksByStatus.has(status)) tasksByStatus.set(status, []);
            tasksByStatus.get(status).push(t);
          }
        });
        
        // Get tasks in destination column
        const destTasks = tasksByStatus.get(destCol.id) || [];
        const destInsertIndex = Math.min(res.destination.index, destTasks.length);
        
        // Calculate overall position in child_order
        let insertPosition = 0;
        for (let i = 0; i < columns.length; i++) {
          const colId = columns[i].id;
          if (colId === destCol.id) {
            insertPosition += destInsertIndex;
            break;
          }
          const colTasks = tasksByStatus.get(colId) || [];
          insertPosition += colTasks.length;
        }
        
        // Insert at new position
        // Ensure task isn't already in destination before adding
        const newDestChildren = (destStoryInGoal.children || []).filter(t => t.id !== taskId);
        const newDestChildOrder = destChildOrder.filter(id => id !== taskId);
        
        // Calculate position in children array (same logic)
        let childrenInsertPosition = 0;
        for (let i = 0; i < columns.length; i++) {
          const colId = columns[i].id;
          if (colId === destCol.id) {
            childrenInsertPosition += destInsertIndex;
            break;
          }
          const colChildren = tasksByStatus.get(colId) || [];
          childrenInsertPosition += colChildren.length;
        }
        
        newDestChildren.splice(childrenInsertPosition, 0, updatedTask);
        newDestChildOrder.splice(insertPosition, 0, taskId);
        
        // Update source story in goals
        updatedGoals[sourceGoalIdx] = {
          ...sourceGoalUpdated,
          stories: sourceGoalUpdated.stories.map((s, idx) => {
            if (idx === sourceStoryIdx) {
              // Double-check we're removing the task
              const finalChildren = (s.children || []).filter(t => t && t.id !== taskId);
              return { 
                ...s, 
                children: finalChildren, 
                child_order: newSourceChildOrder.length > 0 ? newSourceChildOrder : null 
              };
            }
            return s;
          })
        };
        
        // Update destination story in goals
        const destGoalUpdated = updatedGoals[destGoalIdx];
        updatedGoals[destGoalIdx] = {
          ...destGoalUpdated,
          stories: destGoalUpdated.stories.map((s, idx) => {
            if (idx === destStoryIdx) {
              return { 
                ...s, 
                children: newDestChildren, 
                child_order: newDestChildOrder 
              };
            }
            return s;
          })
        };
        
        // Use functional update to ensure we're working with latest state
        setGoals(prevGoals => {
          // Merge our optimistic update with the current state
          const mergedGoals = prevGoals.map((goal, gi) => {
            if (gi === sourceGoalIdx) {
              return updatedGoals[sourceGoalIdx];
            }
            if (gi === destGoalIdx) {
              return updatedGoals[destGoalIdx];
            }
            return goal;
          });
          return mergedGoals;
        });
        
        // Now update database
        try {
          // Update task's status and story_id (keep parent_id as is)
          const updates = { story_id: destStory.id };
          if (sourceCol.id !== destCol.id) {
            updates.status = destCol.id;
          }
          await supabase
            .from("work")
            .update(updates)
            .eq("id", taskId);
          
          // Update source story's child_order
          await supabase
            .from("work")
            .update({ child_order: newSourceChildOrder.length > 0 ? newSourceChildOrder : null })
            .eq("id", sourceStory.id);
          
          // Update destination story's child_order
          await supabase
            .from("work")
            .update({ child_order: newDestChildOrder })
            .eq("id", destStory.id);
        } catch (error) {
          console.error("Error updating task in database:", error);
          // Revert optimistic update by refreshing data
          await fetchData();
        } finally {
          isOptimisticUpdate.current = false;
        }
      } else {
        // If moving within the same story, update child_order optimistically
        const sourceGoalIdx = sourcePath[0];
        const sourceStoryIdxInGoal = sourceStoryIdx;
        const sourceGoal = updatedGoals[sourceGoalIdx];
        const sourceStoryInGoal = sourceGoal.stories[sourceStoryIdxInGoal];
        
        const currentChildOrder = sourceStoryInGoal.child_order || [];
        
        // Remove from current position
        const newChildOrder = currentChildOrder.filter(id => id !== taskId);
        
        // Calculate new position in child_order based on destination column and index
        // Group tasks by status to find insertion point
        const tasksByStatus = new Map();
        (sourceStoryInGoal.children || []).forEach(t => {
          if (t.id !== taskId) {
            const status = t.status || 'TODO';
            if (!tasksByStatus.has(status)) tasksByStatus.set(status, []);
            tasksByStatus.get(status).push(t);
          }
        });
        
        // Get tasks in destination column
        const destTasks = tasksByStatus.get(destCol.id) || [];
        const destInsertIndex = Math.min(res.destination.index, destTasks.length);
        
        // Calculate overall position
        let insertPosition = 0;
        for (let i = 0; i < columns.length; i++) {
          const colId = columns[i].id;
          if (colId === destCol.id) {
            insertPosition += destInsertIndex;
            break;
          }
          const colTasks = tasksByStatus.get(colId) || [];
          insertPosition += colTasks.length;
        }
        
        // Insert at new position
        newChildOrder.splice(insertPosition, 0, taskId);
        
        // Update task's status in local state
        const updatedChildren = (sourceStoryInGoal.children || []).map(t => 
          t.id === taskId ? updatedTask : t
        );
        
        // Reorder children array to match child_order
        const orderedChildren = newChildOrder
          .map(id => updatedChildren.find(t => t.id === id))
          .filter(Boolean);
        const unorderedChildren = updatedChildren.filter(t => !newChildOrder.includes(t.id));
        const finalChildren = [...orderedChildren, ...unorderedChildren];
        
        // Update source story in goals
        updatedGoals[sourceGoalIdx] = {
          ...sourceGoal,
          stories: sourceGoal.stories.map((s, idx) => 
            idx === sourceStoryIdxInGoal
              ? { ...s, children: finalChildren, child_order: newChildOrder }
              : s
          )
        };
        
        setGoals(updatedGoals);
        
        // Now update database
        try {
          const updates = {};
          if (sourceCol.id !== destCol.id) {
            updates.status = destCol.id;
          }
          if (Object.keys(updates).length > 0) {
            await supabase
              .from("work")
              .update(updates)
              .eq("id", taskId);
          }
          
          await supabase
            .from("work")
            .update({ child_order: newChildOrder })
            .eq("id", sourceStory.id);
        } catch (error) {
          console.error("Error updating task in database:", error);
          // Revert optimistic update by refreshing data
          await fetchData();
        } finally {
          isOptimisticUpdate.current = false;
        }
      }

      // Don't refresh data - optimistic update already shows the change
      // If needed for error handling, uncomment:
      // await fetchData();
    },
    [supabase, draggedStory, goals, fetchData, columns]
  );

  const dragEnded = useCallback(
    (res) => {
      // Bail if dropped outside or in source column
      if (!res.destination) return;

      // Indices [goal, story, column, task] for task source and destination
      const sourcePath = res.draggableId.split("-").map((v) => parseInt(v, 10));
      const destPath = res.destination.droppableId.split("-").map((v) => parseInt(v, 10));

      moveDraggable(sourcePath, destPath, res);
    },
    [moveDraggable]
  );

  // Helper to persist collapsed goals to settings
  const persistCollapsedGoals = useCallback(async (newCollapsed) => {
    if (!user?.proj?.id || !user?.id) return;
    const collapsedIds = Object.keys(newCollapsed).filter(id => newCollapsed[id]);
    if (collapsedIds.length === 0) {
      // Remove the settings entry if no collapsed goals
      await supabase
        .from('settings')
        .delete()
        .eq('name', 'collapsedGoals')
        .eq('project_id', user.proj.id)
        .eq('user_id', user.id);
    } else {
      // Upsert as before
      await supabase.from('settings').upsert({
        name: 'collapsedGoals',
        value: JSON.stringify(collapsedIds),
        project_id: user.proj.id,
        user_id: user.id,
      }, { onConflict: 'name, project_id, user_id' });
    }
  }, [supabase, user]);

  // Helper to persist collapsed stories to settings
  const persistCollapsedStories = useCallback(async (newCollapsed) => {
    if (!user?.proj?.id || !user?.id) return;
    const collapsedIds = Object.keys(newCollapsed).filter(id => newCollapsed[id]);
    
    // Check if setting exists
    const { data: existing } = await supabase
      .from('settings')
      .select('id')
      .eq('name', 'collapsedStories')
      .eq('project_id', user.proj.id)
      .eq('user_id', user.id)
      .maybeSingle();

    if (collapsedIds.length === 0) {
      // Remove the settings entry if no collapsed stories
      if (existing) {
        await supabase
          .from('settings')
          .delete()
          .eq('id', existing.id);
      }
    } else {
      const value = JSON.stringify(collapsedIds);
      if (existing) {
        // Update existing
        await supabase
          .from('settings')
          .update({ value })
          .eq('id', existing.id);
      } else {
        // Insert new
        await supabase.from('settings').insert({
          name: 'collapsedStories',
          value,
          project_id: user.proj.id,
          user_id: user.id,
        });
      }
    }
  }, [supabase, user]);

  // Helper to save view mode setting (currently unused - kept for future use)
  // eslint-disable-next-line no-unused-vars
  const saveViewModeSetting = useCallback(async (mode) => {
    if (!user?.proj?.id || !user?.id) return;
    
    if (mode === 'kanban') {
      // Delete the setting when in kanban mode (default)
      const { error } = await supabase.from('settings').delete().eq('name', 'workViewMode').eq('project_id', user.proj.id).eq('user_id', user.id);
      if (error) console.error('Error deleting viewMode setting:', error);
      else {
        // Refresh user data to update cached settings
        refresh();
      }
    } else {
      // Save the setting for list mode
      // Check if viewMode setting already exists for this user/project
      const { data: existingSetting } = await supabase
        .from('settings')
        .select('id')
        .eq('name', 'workViewMode')
        .eq('project_id', user.proj.id)
        .eq('user_id', user.id);
      
      let error;
      if (existingSetting.length > 0) {
        // Update existing setting
        const result = await supabase
          .from('settings')
          .update({ value: mode })
          .eq('id', existingSetting[0].id);
        error = result.error;
      } else {
        // Insert new setting
        const result = await supabase.from('settings').insert({
          name: 'workViewMode',
          value: mode,
          project_id: user.proj.id,
          user_id: user.id,
        });
        error = result.error;
      }
      
      if (error) console.error('Error saving viewMode setting:', error);
      else {
        // Refresh user data to update cached settings
        refresh();
      }
    }
  }, [supabase, user, refresh]);

  // eslint-disable-next-line no-unused-vars
  const getGpt = useCallback(() => {
    const goalIdx = goals.findIndex((g) => g.id === editingTask.goal.id);
    const story = goals[goalIdx].stories.find((sty) => sty.id === editingTask.story.id);

    fetch("/.netlify/functions/gpt", {
      method: "POST",
      body: JSON.stringify({ task: newTitle.current.value.trim(), project_desc: user.proj.description, story: story.title }),
    })
      .then((res) => res.json())
      .then((data) => {
        const _data = JSON.parse(data.message);
        // setGpt removed - function kept for potential future use
        console.log('GPT response:', _data.content);
      });
  }, [editingTask, user, goals]);

  const clearSelection = useCallback(() => {
    setSelectedStory(-1);
    setSelectedTask(-1);
    // Clear editing state when clearing selection
    setEditingStory(false);
    setEditingTask(null);
  }, [setSelectedStory, setSelectedTask]);

  // Navigate up/down within tasks, or between stories
  const arrowUp = useCallback(() => {
    if (!editingStory && !editingTask && viewMode === 'kanban') {
      if (selectedTask > -1) {
        // Task-level navigation - move up within the same column
        const flatS = flatStories;
        // Safety check: ensure selectedStory is within bounds
        if (selectedStory < 0 || selectedStory >= flatS.length) return;
        const { gi, si } = flatS[selectedStory];
        const goal = goals[gi];
        const storyOrder = goal.workitems_order || [];
        const storyId = storyOrder[si];
        const currentStory = goal.stories.find((st) => st.id === storyId);
        
        if (!currentStory || !currentStory.children) {
          return;
        }
        
        // Don't allow task selection if story is collapsed
        if (collapsedStories[currentStory.id]) {
          setSelectedTask(-1);
          return;
        }
        
        // Find which column the current task is in and its position within that column
        let currentColIdx = -1;
        let taskIdxInCol = -1;
        let taskOffset = 0;
        
        for (let colIdx = 0; colIdx < columns.length; colIdx++) {
          const colTasks = currentStory.children.filter(task => task.status === columns[colIdx].id);
          if (selectedTask >= taskOffset && selectedTask < taskOffset + colTasks.length) {
            currentColIdx = colIdx;
            taskIdxInCol = selectedTask - taskOffset;
            break;
          }
          taskOffset += colTasks.length;
        }
        
        // Move up within the same column
        if (currentColIdx >= 0 && taskIdxInCol > 0) {
          // Move to previous task in the same column
          setSelectedTask(taskOffset + (taskIdxInCol - 1));
        }
        // If taskIdxInCol === 0, stay on the first task in the column (don't move)
      } else if (selectedStory > -1) {
        // Story-level navigation - move up to previous visible story
        // flatStories now only contains visible stories, so we can navigate directly
        if (selectedStory > 0) {
          // Move to previous visible story
          setSelectedStory(selectedStory - 1);
        }
        // If at first visible story, do nothing - just return to keep the selection
      } else {
        // Nothing selected - select top visible story
        if (flatStories.length > 0) {
          setSelectedStory(0);
        }
      }
    }
  }, [editingStory, editingTask, selectedTask, selectedStory, setSelectedStory, setSelectedTask, goals, columns, viewMode, collapsedStories, flatStories]);

  const arrowDown = useCallback(() => {
    if (!editingStory && !editingTask && viewMode === 'kanban') {
      if (selectedTask > -1) {
        // Task-level navigation - move down within the same column
        const flatS = flatStories;
        // Safety check: ensure selectedStory is within bounds
        if (selectedStory < 0 || selectedStory >= flatS.length) return;
        const { gi, si } = flatS[selectedStory];
        const goal = goals[gi];
        const storyOrder = goal.workitems_order || [];
        const storyId = storyOrder[si];
        const currentStory = goal.stories.find((st) => st.id === storyId);
        
        if (!currentStory || !currentStory.children) {
          return;
        }
        
        // Don't allow task selection if story is collapsed
        if (collapsedStories[currentStory.id]) {
          setSelectedTask(-1);
          return;
        }
        
        // Find which column the current task is in and its position within that column
        let currentColIdx = -1;
        let taskIdxInCol = -1;
        let taskOffset = 0;
        
        for (let colIdx = 0; colIdx < columns.length; colIdx++) {
          const colTasks = currentStory.children.filter(task => task.status === columns[colIdx].id);
          if (selectedTask >= taskOffset && selectedTask < taskOffset + colTasks.length) {
            currentColIdx = colIdx;
            taskIdxInCol = selectedTask - taskOffset;
            break;
          }
          taskOffset += colTasks.length;
        }
        
        // Move down within the same column
        if (currentColIdx >= 0) {
          const colTasks = currentStory.children.filter(task => task.status === columns[currentColIdx].id);
          if (taskIdxInCol < colTasks.length - 1) {
            // Move to next task in the same column
            setSelectedTask(taskOffset + (taskIdxInCol + 1));
          }
          // If at the last task in the column, stay on it (don't move)
        }
      } else if (selectedStory > -1) {
        // Story-level navigation - move down to next visible story
        // flatStories now only contains visible stories, so we can navigate directly
        if (selectedStory < flatStories.length - 1) {
          // Move to next visible story
          setSelectedStory(selectedStory + 1);
        }
        // If at last visible story, do nothing - just return to keep the selection
      } else {
        // Nothing selected - select top visible story
        if (flatStories.length > 0) {
          setSelectedStory(0);
        }
      }
    }
  }, [editingStory, editingTask, selectedTask, selectedStory, setSelectedStory, setSelectedTask, goals, columns, viewMode, collapsedStories, flatStories]);

  // Navigate left/right between columns in the same story, or collapse/expand stories
  const arrowLeft = useCallback(() => {
    if (!editingStory && !editingTask && viewMode === 'kanban') {
      if (selectedTask > -1) {
        // Task-level navigation - move left between columns
        const flatS = flatStories;
        if (selectedStory < 0 || selectedStory >= flatS.length) return;
        const { gi, si } = flatS[selectedStory];
        const goal = goals[gi];
        const storyOrder = goal.workitems_order || [];
        const storyId = storyOrder[si];
        const currentStory = goal.stories.find((st) => st.id === storyId);
        
        if (!currentStory || !currentStory.children) {
          return;
        }
        
        // Don't allow task selection if story is collapsed
        if (collapsedStories[currentStory.id]) {
          setSelectedTask(-1);
          return;
        }
        
        // Calculate which column and task position we're at
        let currentColIdx = -1;
        let taskIdxInCol = -1;
        let taskOffset = 0;
        
        for (let colIdx = 0; colIdx < columns.length; colIdx++) {
          const colTasks = currentStory.children.filter(task => task.status === columns[colIdx].id);
          if (selectedTask >= taskOffset && selectedTask < taskOffset + colTasks.length) {
            currentColIdx = colIdx;
            taskIdxInCol = selectedTask - taskOffset;
            break;
          }
          taskOffset += colTasks.length;
        }
        
        // Find the next column to the left that has tasks
        if (currentColIdx > 0) {
          for (let colIdx = currentColIdx - 1; colIdx >= 0; colIdx--) {
            const colTasks = currentStory.children.filter(task => task.status === columns[colIdx].id);
            if (colTasks.length > 0) {
              // Calculate new task index
              let newTaskOffset = 0;
              for (let k = 0; k < colIdx; k++) {
                newTaskOffset += currentStory.children.filter(task => task.status === columns[k].id).length;
              }
              const newTaskIndex = Math.min(taskIdxInCol, colTasks.length - 1);
              setSelectedTask(newTaskOffset + newTaskIndex);
              break;
            }
          }
        }
      } else if (selectedStory > -1) {
        // Story-level navigation - collapse the story
        const flatS = flatStories;
        if (selectedStory < 0 || selectedStory >= flatS.length) return;
        const { gi, si } = flatS[selectedStory];
        const goal = goals[gi];
        const storyOrder = goal.workitems_order || [];
        const storyId = storyOrder[si];
        const story = goal.stories.find((st) => st.id === storyId);
        
        if (story) {
          // Clear task selection if collapsing a story that has a selected task
          if (selectedTask > -1) {
            setSelectedTask(-1);
          }
          
          setCollapsedStories((prev) => {
            const updated = { ...prev, [story.id]: true };
            persistCollapsedStories(updated);
            return updated;
          });
        }
      } else {
        // Nothing selected - select top story
        const flatS = flatStories;
        if (flatS.length > 0) {
          setSelectedStory(0);
        }
      }
    }
  }, [editingStory, editingTask, selectedTask, selectedStory, setSelectedStory, setSelectedTask, goals, columns, viewMode, persistCollapsedStories, collapsedStories, flatStories]);

  const arrowRight = useCallback(() => {
    if (!editingStory && !editingTask && viewMode === 'kanban') {
      if (selectedTask > -1) {
        // Task-level navigation - move right between columns
        const flatS = flatStories;
        if (selectedStory < 0 || selectedStory >= flatS.length) return;
        const { gi, si } = flatS[selectedStory];
        const goal = goals[gi];
        const storyOrder = goal.workitems_order || [];
        const storyId = storyOrder[si];
        const currentStory = goal.stories.find((st) => st.id === storyId);
        
        if (!currentStory || !currentStory.children) {
          return;
        }
        
        // Don't allow task selection if story is collapsed
        if (collapsedStories[currentStory.id]) {
          setSelectedTask(-1);
          return;
        }
        
        // Calculate which column and task position we're at
        let currentColIdx = -1;
        let taskIdxInCol = -1;
        let taskOffset = 0;
        
        for (let colIdx = 0; colIdx < columns.length; colIdx++) {
          const colTasks = currentStory.children.filter(task => task.status === columns[colIdx].id);
          if (selectedTask >= taskOffset && selectedTask < taskOffset + colTasks.length) {
            currentColIdx = colIdx;
            taskIdxInCol = selectedTask - taskOffset;
            break;
          }
          taskOffset += colTasks.length;
        }
        
        // Find the next column to the right that has tasks
        if (currentColIdx >= 0 && currentColIdx < columns.length - 1) {
          for (let colIdx = currentColIdx + 1; colIdx < columns.length; colIdx++) {
            const colTasks = currentStory.children.filter(task => task.status === columns[colIdx].id);
            if (colTasks.length > 0) {
              // Calculate new task index
              let newTaskOffset = 0;
              for (let k = 0; k < colIdx; k++) {
                newTaskOffset += currentStory.children.filter(task => task.status === columns[k].id).length;
              }
              const newTaskIndex = Math.min(taskIdxInCol, colTasks.length - 1);
              setSelectedTask(newTaskOffset + newTaskIndex);
              break;
            }
          }
        }
      } else if (selectedStory > -1) {
        // Story-level navigation - expand the story
        const flatS = flatStories;
        if (selectedStory < 0 || selectedStory >= flatS.length) return;
        const { gi, si } = flatS[selectedStory];
        const goal = goals[gi];
        const storyOrder = goal.workitems_order || [];
        const storyId = storyOrder[si];
        const story = goal.stories.find((st) => st.id === storyId);
        
        if (story) {
          setCollapsedStories((prev) => {
            const updated = { ...prev, [story.id]: false };
            persistCollapsedStories(updated);
            return updated;
          });
        }
      } else {
        // Nothing selected - select top story
        const flatS = flatStories;
        if (flatS.length > 0) {
          setSelectedStory(0);
        }
      }
    }
  }, [editingStory, editingTask, selectedTask, selectedStory, setSelectedStory, setSelectedTask, goals, columns, viewMode, persistCollapsedStories, collapsedStories, flatStories]);

  // Consolidated arrow key handlers
  useKey("ArrowUp", false, (e) => {
    if (searchVisible) return;
    // Don't prevent default if user is typing in an input field
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
      return;
    }
    e.preventDefault();
    arrowUp();
  });

  useKey("ArrowLeft", false, (e) => {
    if (searchVisible) return;
    // Don't prevent default if user is typing in an input field
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
      return;
    }
    e.preventDefault();
    arrowLeft();
  });

  useKey("ArrowDown", false, (e) => {
    if (searchVisible) return;
    // Don't prevent default if user is typing in an input field
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
      return;
    }
    e.preventDefault();
    arrowDown();
  });

  useKey("ArrowRight", false, (e) => {
    if (searchVisible) return;
    // Don't prevent default if user is typing in an input field
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
      return;
    }
    e.preventDefault();
    arrowRight();
  });

  useKey("ArrowRight", "alt", null, () => {
    if (searchVisible) return;
    if (!editingStory && !editingTask && selectedTask > -1) {
      const flatS = flatStories;
      if (selectedStory < 0 || selectedStory >= flatS.length) return;
      const { gi, si } = flatS[selectedStory];
      const goal = goals[gi];
          const storyOrder = goal.workitems_order || [];
      const storyId = storyOrder[si];
      const currentStory = goal.stories.find((st) => st.id === storyId);
      if (!currentStory || !currentStory.children) return;
      
      // Don't allow task selection if story is collapsed
      if (collapsedStories[currentStory.id]) {
        setSelectedTask(-1);
        return;
      }
      
      // Build flat array of all tasks across all columns for this story
      const allTasks = [];
      let currentColIdx = 0;
      let taskIdxInCol = 0;
      let taskFound = false;
      
      for (let colIdx = 0; colIdx < columns.length; colIdx++) {
        const colId = columns[colIdx].id;
        const colTasks = currentStory.children.filter(t => (t.status || 'TODO') === colId);
        
        for (let ti = 0; ti < colTasks.length; ti++) {
          if (allTasks.length === selectedTask) {
            currentColIdx = colIdx;
            taskIdxInCol = ti;
            taskFound = true;
            break;
          }
          allTasks.push({ colIdx, taskIdx: ti, task: colTasks[ti] });
        }
        if (taskFound) break;
      }
      
      if (!taskFound) return;
      
      // Move to next column
      const dstColIndex = Math.min(currentColIdx + 1, columns.length - 1);
      
      // If already at last column, can't move right
      if (dstColIndex === currentColIdx) return;
      
      const dstColId = columns[dstColIndex].id;
      const dstColTasks = currentStory.children.filter(t => (t.status || 'TODO') === dstColId);
      
      const src = [gi, si, currentColIdx, taskIdxInCol];
      const dst = [gi, si, dstColIndex];
      
      // Calculate new selectedTask index after move
      // After moving, the task will be at the end of destination column
      // We need to count tasks in columns before destination, accounting for the removal from source column
      let newTaskOffset = 0;
      for (let k = 0; k < dstColIndex; k++) {
        const colTasks = currentStory.children.filter(t => (t.status || 'TODO') === columns[k].id);
        // If this is the source column, subtract 1 because we're removing the task
        if (k === currentColIdx) {
          newTaskOffset += colTasks.length - 1;
        } else {
          newTaskOffset += colTasks.length;
        }
      }
      // Task will be at the end of destination column
      const newSelectedTask = newTaskOffset + dstColTasks.length;
      
      // Clear any editing state and update selectedTask immediately so it doesn't lose highlight
      setEditingTask(null);
      setSelectedTask(newSelectedTask);
      
      moveDraggable(src, dst, {
        type: "task",
        destination: {
          droppableId: dst.join("-"),
          index: dstColTasks.length,
        },
      });
    }
  });

  useKey("ArrowLeft", "alt", null, () => {
    if (searchVisible) return;
    if (!editingStory && !editingTask && selectedTask > -1) {
      const flatS = flatStories;
      if (selectedStory < 0 || selectedStory >= flatS.length) return;
      const { gi, si } = flatS[selectedStory];
      const goal = goals[gi];
          const storyOrder = goal.workitems_order || [];
      const storyId = storyOrder[si];
      const currentStory = goal.stories.find((st) => st.id === storyId);
      if (!currentStory || !currentStory.children) return;
      
      // Don't allow task selection if story is collapsed
      if (collapsedStories[currentStory.id]) {
        setSelectedTask(-1);
        return;
      }
      
      // Build flat array of all tasks across all columns for this story
      const allTasks = [];
      let currentColIdx = 0;
      let taskIdxInCol = 0;
      let taskFound = false;
      
      for (let colIdx = 0; colIdx < columns.length; colIdx++) {
        const colId = columns[colIdx].id;
        const colTasks = currentStory.children.filter(t => (t.status || 'TODO') === colId);
        
        for (let ti = 0; ti < colTasks.length; ti++) {
          if (allTasks.length === selectedTask) {
            currentColIdx = colIdx;
            taskIdxInCol = ti;
            taskFound = true;
            break;
          }
          allTasks.push({ colIdx, taskIdx: ti, task: colTasks[ti] });
        }
        if (taskFound) break;
      }
      
      if (!taskFound) return;
      
      // Move to previous column
      const dstColIndex = Math.max(currentColIdx - 1, 0);
      
      // If already at first column, can't move left - keep highlight and do nothing
      if (dstColIndex === currentColIdx) return;
      
      const dstColId = columns[dstColIndex].id;
      const dstColTasks = currentStory.children.filter(t => (t.status || 'TODO') === dstColId);
      
      const src = [gi, si, currentColIdx, taskIdxInCol];
      const dst = [gi, si, dstColIndex];
      
      // Calculate new selectedTask index after move (task will be at end of destination column)
      // Sum tasks in all columns before destination
      let newTaskOffset = 0;
      for (let k = 0; k < dstColIndex; k++) {
        newTaskOffset += currentStory.children.filter(t => (t.status || 'TODO') === columns[k].id).length;
      }
      // After move, task will be added at the end of destination column
      // The task will be at position dstColTasks.length within that column (0-indexed)
      const newSelectedTask = newTaskOffset + dstColTasks.length;
      
      // Clear any editing state and update selectedTask immediately so it doesn't lose highlight
      setEditingTask(null);
      setSelectedTask(newSelectedTask);
      
      moveDraggable(src, dst, {
        type: "task",
        destination: {
          droppableId: dst.join("-"),
          index: dstColTasks.length,
        },
      });
    }
  });

  useKey("Enter", false, (e) => {
    if (searchVisible) return;
      
      if (!editingStory && !editingTask) {
        if (selectedStory === -1 && selectedTask === -1) {
          // Nothing selected - select top visible story
          const flatS = flatStories;
          // Find first visible story that actually exists (skip hidden goals and missing stories)
          let firstVisibleIndex = -1;
          for (let idx = 0; idx < flatS.length; idx++) {
            const { gi, si } = flatS[idx];
            const goal = goals[gi];
            // Skip hidden goals
            if (goalsHidden.includes(goal.id)) {
              continue;
            }
            // Check if story actually exists
            const storyOrder = goal.workitems_order || [];
            if (si < storyOrder.length) {
              const storyId = storyOrder[si];
              const story = goal.stories.find((st) => st.id === storyId);
              if (story) {
                firstVisibleIndex = idx;
                break;
              }
            }
          }
          if (firstVisibleIndex >= 0) {
            setSelectedStory(firstVisibleIndex);
          }
        } else if (selectedTask === -1 && selectedStory > -1) {
          // Story is selected - enter story and highlight first task
          const flatS = flatStories;
          const { gi, si } = flatS[selectedStory];
          const goal = goals[gi];
          const storyOrder = goal.workitems_order || [];
          const storyId = storyOrder[si];
          const currentStory = goal.stories.find((st) => st.id === storyId);
          
          // Don't allow task selection if story is collapsed
          if (!currentStory || collapsedStories[currentStory.id]) {
            return;
          }
          
          if (currentStory && currentStory.children && currentStory.children.length > 0) {
            // Story has tasks - select first task
            setSelectedTask(0);
          }
          // If no tasks, stay on story (user can use Ctrl+Enter to create task)
        } else if (selectedTask > -1) {
          // Task is selected - this is handled in Task.js useKey handler to open modal
          // Don't do anything here, let Task.js handle it
      }
    }
  });

  useKey("Enter", true, (e) => {
      // Kanban view - Cmd/Ctrl+Enter to add task or story (but not Shift+Cmd/Ctrl+Enter)
      // Don't trigger if Shift is also pressed (that's for modal editing)
      if (e.shiftKey) return;
      if (!editingStory && !editingStoryModal && !editingTask && !searchVisible) {
        if (selectedStory === -1 && selectedTask === -1) {
          // Nothing selected - create new story
          addItemForKanban();
        } else if (selectedStory > -1 && selectedTask === -1) {
          // Story is selected - add task to first column
          const flatS = goals.flatMap((g, gi) => {
            const storyOrder = g.workitems_order || [];
            return storyOrder.map((storyId) => {
              const story = g.stories.find(s => s.id === storyId);
              return { gi, storyId, story };
            }).filter(item => item.story);
          });
          
          if (selectedStory < flatS.length) {
            const { storyId, story } = flatS[selectedStory];
            
            // Don't add task if story is completed
            if (storyId && story?.status !== "COMPLETE") {
              // Add task to first column (TODO status)
              const firstColumn = columns[0];
              addTaskToStory(storyId, firstColumn.id);
            }
          }
        } else if (selectedStory > -1 && selectedTask > -1) {
          // Task is selected - add task to the same story
          // Safety check: ensure selectedStory is within bounds
          if (selectedStory < flatStories.length) {
            const { gi, si } = flatStories[selectedStory];
            const goal = goals[gi];
            const storyOrder = goal.workitems_order || [];
            const storyId = storyOrder[si];
            const currentStory = goal.stories.find((st) => st.id === storyId);
            
            // Don't add task if story is completed
            if (storyId && currentStory?.status !== "COMPLETE") {
              // Add task to first column (TODO status)
              const firstColumn = columns[0];
              addTaskToStory(storyId, firstColumn.id);
            }
          }
        }
      }
  });

  // Set input value when modal opens
  useEffect(() => {
    if (editingStoryModal && newTitle.current) {
      newTitle.current.value = editingStoryModal.title || "";
    }
  }, [editingStoryModal]);

  // Shift+Cmd/Ctrl+Enter to edit selected story in modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Shift+Cmd/Ctrl+Enter
      if (e.key === "Enter" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
        // Don't trigger if search is visible or already editing
        if (searchVisible || editingStory || editingStoryModal || editingTask) return;
        
        // Check if a story is selected (not a task)
        if (selectedStory >= 0 && selectedTask === -1) {
          e.preventDefault();
          e.stopPropagation();
          
          // Find the selected story
          const flatS = flatStories;
          if (selectedStory < flatS.length) {
            const { gi, si } = flatS[selectedStory];
            const goal = goals[gi];
            const storyOrder = goal.workitems_order || [];
            if (si < storyOrder.length) {
              const storyId = storyOrder[si];
              const story = goal.stories.find((st) => st.id === storyId);
              
              if (story) {
                // Trigger modal story editing
                setEditingStoryModal({ goal: { id: goal.id }, id: storyId, title: story.title });
              }
            }
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedStory, selectedTask, searchVisible, editingStory, editingStoryModal, editingTask, flatStories, goals]);

  useKey("Backspace", false, (e) => {
      // Kanban view
      if (!editingStory && !editingTask && !searchVisible) {
        // Use the same pattern as arrow navigation to find the story/task
        const flatS = flatStories;
        
        if (selectedStory >= 0 && selectedStory < flatS.length) {
          const { gi, si } = flatS[selectedStory];
          const goal = goals[gi];
          const storyOrder = goal.workitems_order || [];
          const storyId = storyOrder[si];
          const currentStory = goal.stories.find((st) => st.id === storyId);
          
          if (selectedTask > -1) {
            // Delete task
            if (currentStory && currentStory.children) {
              // Build array of all tasks across all columns (same pattern as arrowUp/arrowDown)
              const allTasks = [];
              columns.forEach((col, colIdx) => {
                const colTasks = currentStory.children.filter(task => task.status === col.id);
                colTasks.forEach((task, taskIdx) => {
                  allTasks.push({ colIdx, taskIdx, task });
                });
              });
              
              // Get the task at selectedTask index
              if (selectedTask >= 0 && selectedTask < allTasks.length) {
                const selectedTaskObj = allTasks[selectedTask];
                deleteTask(selectedTaskObj.task.id);
              }
            }
          } else if (selectedStory > -1 && currentStory) {
            // Delete story
            deleteStory([gi, si], currentStory.id, currentStory.title);
        }
      }
    }
  });





  // Search functionality - detect when user starts typing
  const typingBufferRef = useRef("");
  const typingTimeoutRef = useRef();

  // Handle any key press for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger search if we're in editing modes or if search is already visible
      if (editingStory || editingTask || searchVisible) return;
      
      // Don't trigger if we're typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true') return;
      
      // Don't trigger on special keys
      const specialKeys = ['Escape', 'Tab', 'Enter', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Shift', 'Control', 'Alt', 'Meta'];
      if (specialKeys.includes(e.key)) return;
      
      // Don't trigger on modifier keys
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      
      // Only trigger on letter keys (a-z, A-Z, and accented characters like å, ä, ö, etc.)
      // This regex matches any letter character including Swedish characters (å, ä, ö)
      const isLetter = /^[a-zA-ZåäöÅÄÖ]$/.test(e.key);
      if (!isLetter) return;
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Add character to buffer
      const newBuffer = typingBufferRef.current + e.key;
      typingBufferRef.current = newBuffer;
      
      // Set timeout to clear buffer
      typingTimeoutRef.current = setTimeout(() => {
        typingBufferRef.current = "";
      }, 1000);
      
      // If buffer has at least 1 character, show search
      if (newBuffer.length >= 1) {
        e.preventDefault();
        e.stopPropagation();
        setSearchVisible(true);
        setSearchText(newBuffer);
      }
    };

    document.addEventListener('keydown', handleKeyDown); // Remove capture phase
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [editingStory, editingTask, searchVisible]);

  // Unified Escape handler with priority: search > kanban view
  useKey("Escape", false, (e) => {
    // Priority 1: Handle search
    if (searchVisible) {
      e.preventDefault();
      e.stopPropagation();
      setSearchVisible(false);
      setSearchText("");
      setHighlightedItem(null);
      typingBufferRef.current = "";
      return;
    }

    // Priority 2: Handle kanban view
      if (editingStory || editingTask) {
        // If editing, just exit edit mode
        setEditingStory(false);
        setEditingTask(null);
      } else {
        if (selectedTask > -1) {
          // Task is selected - deselect both task and story
          setSelectedTask(-1);
          setSelectedStory(-1);
        } else if (selectedStory > -1) {
          // Story is selected - deselect story
          setSelectedStory(-1);
        }
        // Clear editing state when deselecting
        setEditingStory(false);
        setEditingTask(null);
    }
  });

  // List view functions removed - starting fresh
  // All list view functions have been removed: submitItem, deselectItems, deleteItem, convertGoalsToWorkItems, buildTree, flattenTree, submitItemMove, addItem

  // Removed all list view functions: deleteItem, convertGoalsToWorkItems, buildTree, flattenTree, submitItemMove, addItem

  // Convert a story to a task under another story (currently unused - kept for future use)
  // eslint-disable-next-line no-unused-vars
  const convertStoryToTask = useCallback(async (storyItem, parentStoryItem) => {
    try {
      // Update the work item: set story_id to parent story, parent_id to null, status to first column (TODO)
      const goal = goals.find(g => g.id === storyItem.goalId);
      const parentStory = goal?.stories.find(s => s.id === parentStoryItem.storyId);
      
      if (!parentStory) {
        console.error('Parent story not found');
        return;
      }
      
      // Get first column for default status
      const firstColumnId = columns[0]?.id || 'TODO';
      
      // Add story to parent's child_order
      const currentChildOrder = parentStory.child_order || [];
      const updatedChildOrder = [...currentChildOrder, storyItem.storyId];
      await supabase
        .from('work')
        .update({ child_order: updatedChildOrder })
        .eq('id', parentStory.id);
      
      // Update the story to become a task: set story_id, parent_id to null, and status
      await supabase
        .from('work')
        .update({
          story_id: parentStory.id,
          parent_id: null,
          status: firstColumnId
        })
        .eq('id', storyItem.storyId);
      
      // Update goal's workitems_order to remove this item (since it's now a task, not a story)
      const currentOrder = goal.workitems_order || [];
      const updatedOrder = currentOrder.filter(id => id !== storyItem.storyId);
      
      await supabase
        .from('goals')
        .update({ workitems_order: updatedOrder.length > 0 ? updatedOrder : null })
        .eq('id', goal.id);
      
      // Refresh data
      await fetchData();
      
    } catch (error) {
      console.error('Error converting story to task:', error);
    }
  }, [goals, supabase, fetchData, columns]);

  // Convert a task to a story in the same goal (currently unused - kept for future use)
  // eslint-disable-next-line no-unused-vars
  const convertTaskToStory = useCallback(async (taskItem) => {
    try {
      // Find the task and its parent story
      const goal = goals.find(g => g.id === taskItem.goalId);
      const parentStory = goal?.stories.find(s => s.id === taskItem.storyId);
      const task = parentStory?.children?.find(t => t.id === taskItem.taskId);
      
      if (!task) {
        console.error('Task not found');
        return;
      }
      
      // Update parent story's child_order to remove this task (parent_id will be set to null by trigger)
      const currentChildOrder = parentStory.child_order || [];
      const updatedChildOrder = currentChildOrder.filter(id => id !== taskItem.taskId);
      
      await supabase
        .from('work')
        .update({ child_order: updatedChildOrder.length > 0 ? updatedChildOrder : null })
        .eq('id', parentStory.id);
      
      // Update the work item: clear status and set type to story
      await supabase
        .from('work')
        .update({
          status: null
        })
        .eq('id', taskItem.taskId);
      
      // Update goal's workitems_order to include this item as a story
      const currentOrder = goal.workitems_order || [];
      const updatedOrder = [...currentOrder, taskItem.taskId];
      
      await supabase
        .from('goals')
        .update({ workitems_order: updatedOrder })
        .eq('id', goal.id);
      
      // Refresh data
      await fetchData();
      
    } catch (error) {
      console.error('Error converting task to story:', error);
    }
  }, [goals, supabase, fetchData]);


  // Search functionality
  const [highlightedItem, setHighlightedItem] = useState(null);
  
  // Check if search string matches text with fuzzy/acronym matching
  // Returns true if all characters in search appear in order in text
  const fuzzyMatch = (text, search) => {
    const textLower = text.toLowerCase();
    const searchLower = search.toLowerCase();
    let textIndex = 0;
    
    for (let i = 0; i < searchLower.length; i++) {
      const searchChar = searchLower[i];
      const foundIndex = textLower.indexOf(searchChar, textIndex);
      if (foundIndex === -1) {
        return false;
      }
      textIndex = foundIndex + 1;
    }
    
    return true;
  };

  // Calculate match score for finding the best match
  const calculateMatchScore = useCallback((text, search) => {
    const textLower = text.toLowerCase();
    const searchLower = search.toLowerCase();
    
    // Exact match gets highest score
    if (textLower === searchLower) return 100;
    
    // Starts with search gets high score
    if (textLower.startsWith(searchLower)) return 90;
    
    // Word boundary match gets higher score than general contains
    const words = textLower.split(/\s+/);
    const wordMatch = words.some(word => word.startsWith(searchLower));
    if (wordMatch) return 80;
    
    // Contains search gets medium score
    if (textLower.includes(searchLower)) return 60;
    
    // Fuzzy/acronym match gets lower score
    if (fuzzyMatch(text, search)) return 40;
    
    return 0;
  }, []);
  
  const handleSearch = useCallback((text) => {
    if (!text.trim()) {
      setHighlightedItem(null);
      return;
    }

    const results = [];
    const searchLower = text.toLowerCase();
    let bestMatch = null;
    let bestScore = -1;

    // Search in kanban view
    if (goals) {
      goals.forEach(goal => {
        const storyOrder = goal.workitems_order || [];
        if (storyOrder && goal.stories) {
          storyOrder.forEach(storyId => {
            const story = goal.stories.find(s => s.id === storyId);
            if (story) {
              // Skip completed stories if they're hidden
              if (!showCompletedStories && story.status === "COMPLETE") {
                return;
              }
              
              const textLower = story.title.toLowerCase();
              const matches = textLower.includes(searchLower) || fuzzyMatch(story.title, searchLower);
              if (matches) {
                const score = calculateMatchScore(story.title, searchLower);
                const item = {
                  type: 'story',
                  id: story.id,
                  title: story.title,
                  goalId: goal.id,
                  storyId: story.id,
                  score: score
                };
                results.push(item);
                
                if (score > bestScore) {
                  bestScore = score;
                  bestMatch = item;
                }
              }
            }

            // Search through tasks in story.children (same as navigation uses)
            // Only search tasks if story is visible (not completed when hidden)
            if (story && story.children && (showCompletedStories || story.status !== "COMPLETE")) {
              story.children.forEach(task => {
                if (task && task.title) {
                  const textLower = task.title.toLowerCase();
                  const matches = textLower.includes(searchLower) || fuzzyMatch(task.title, searchLower);
                  if (matches) {
                    // Find which column this task belongs to based on status
                    const taskStatus = task.status || 'TODO';
                    const column = columns.find(col => col.id === taskStatus);
                    
                    const score = calculateMatchScore(task.title, searchLower);
                    const item = {
                      type: 'task',
                      id: task.id,
                      title: task.title,
                      goalId: goal.id,
                      storyId: story.id,
                      columnId: taskStatus,
                      columnName: column?.title || taskStatus,
                      score: score
                    };
                    results.push(item);
                    
                    if (score > bestScore) {
                      bestScore = score;
                      bestMatch = item;
                    }
                  }
                }
              });
            }
          });
        }
      });
    }

    setHighlightedItem(bestMatch);
  }, [goals, columns, calculateMatchScore, showCompletedStories]);

  // Separate effect to handle UI selection when highlighted item changes
  useEffect(() => {
    if (searchVisible) {
      if (highlightedItem) {
          // For kanban view, we need to find and select the story or task
          if (highlightedItem.type === 'story') {
            // Use flatStories to find the correct index (respects completed filter)
            const flatS = flatStories;
            const storyIndex = flatS.findIndex(({ gi, si }) => {
              const goal = goals[gi];
              const storyOrder = goal.workitems_order || [];
              return storyOrder[si] === highlightedItem.storyId;
            });
            if (storyIndex !== -1) {
              setSelectedStory(storyIndex);
              setSelectedTask(-1);
            }
          } else if (highlightedItem.type === 'task') {
            // Find the story that contains this task using flatStories
            const flatS = flatStories;
            let storyIndex = -1;
            let targetStory = null;
            for (let idx = 0; idx < flatS.length; idx++) {
              const { gi, si } = flatS[idx];
              const goal = goals[gi];
              const storyOrder = goal.workitems_order || [];
              const storyId = storyOrder[si];
              if (storyId === highlightedItem.storyId) {
                storyIndex = idx;
                targetStory = goal.stories.find(s => s.id === storyId);
                break;
              }
            }
            if (storyIndex !== -1 && targetStory) {
              setSelectedStory(storyIndex);
              // Find the task within the story (tasks are in story.children, filtered by status)
              let taskIndex = 0;
              for (let cIdx = 0; cIdx < columns.length; cIdx++) {
                const col = columns[cIdx];
                const colTasks = targetStory.children ? targetStory.children.filter(t => t.status === col.id) : [];
                const taskIdxInCol = colTasks.findIndex(t => t.id === highlightedItem.id);
                if (taskIdxInCol !== -1) {
                  taskIndex += taskIdxInCol;
                  setSelectedTask(taskIndex);
                  break;
                }
                taskIndex += colTasks.length;
            }
          }
        }
      } else {
        // When highlightedItem is null, clear selections
          setSelectedStory(-1);
          setSelectedTask(-1);
          // Clear editing state when clearing selection
          setEditingStory(false);
          setEditingTask(null);
        }
      }
  }, [highlightedItem, searchVisible, goals, columns, flatStories]);

  // Scroll selected story into view only if it's out of view
  useEffect(() => {
    // Only scroll when a story is selected (not a task)
    if (selectedStory >= 0 && selectedTask === -1 && !editingStory && !editingTask) {
      const storyRef = storyRefs.current[selectedStory];
      if (storyRef) {
        const rect = storyRef.getBoundingClientRect();
        const viewportTop = 0;
        const viewportBottom = window.innerHeight || document.documentElement.clientHeight;
        
        // Check if story is completely out of view (above or below viewport)
        // Only scroll if the story is not visible at all
        if (rect.bottom < viewportTop || rect.top > viewportBottom) {
          storyRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }, [selectedStory, selectedTask, editingStory, editingTask]);

  // eslint-disable-next-line no-unused-vars
  const highlightText = (text, searchText, shouldHighlight = false) => {
    if (!searchText.trim() || !shouldHighlight) return text;
    
    const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#ffeb3b', color: '#000', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : part
    );
  };

  // Check if an item should be highlighted (currently unused - kept for future use)
  // eslint-disable-next-line no-unused-vars
  const shouldHighlightItem = (item) => {
    if (!highlightedItem || !searchText.trim()) return false;
    
      // For kanban view, check if this is the highlighted story or task
      if (item.type === 'story') {
        return highlightedItem.type === 'story' && item.id === highlightedItem.storyId;
      } else if (item.type === 'task') {
        return highlightedItem.type === 'task' && item.id === highlightedItem.taskId;
    }
    return false;
  };

  //----------------------------------------------------

  useLayoutEffect(() => {
    fetchData();
    window.addEventListener("mousedown", clearSelection);
    return () => window.removeEventListener("mousedown", clearSelection);
  }, [fetchData, clearSelection]);

  // Load showCompletedStories from settings when user/project changes
  useEffect(() => {
    // But don't override if user is actively changing the toggle
    if (user?.proj?.settings && !userChangingCompletedToggle.current) {
      const savedValue = user.proj.settings.find((s) => s.name === "workShowCompletedStories")?.value;
      // Load from settings: if setting exists and is 'true', set to true; otherwise keep default false
      if (savedValue === 'true') {
        setShowCompletedStories(true);
      }
      // If no setting or value is not 'true', keep default false (no need to set explicitly)
    }
  }, [user?.proj?.settings]);

  // Load collapsed goals from settings on mount
  useEffect(() => {
    async function loadCollapsedGoals() {
      if (!supabase || !user?.proj?.id || !user?.id) return;
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('name', 'collapsedGoals')
        .eq('project_id', user.proj.id)
        .eq('user_id', user.id);
      if (data && data.value) {
        try {
          const collapsedArr = JSON.parse(data.value);
          const collapsedObj = {};
          collapsedArr.forEach(id => { collapsedObj[id] = true; });
          setCollapsedGoals(collapsedObj);
        } catch (e) {
          // fallback: do nothing
        }
      }
    }
    loadCollapsedGoals();
  }, [supabase, user]);

  // Load collapsed stories from settings on mount
  useEffect(() => {
    async function loadCollapsedStories() {
      if (!supabase || !user?.proj?.id || !user?.id) return;
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('name', 'collapsedStories')
        .eq('project_id', user.proj.id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (data && data.value) {
        try {
          const collapsedArr = JSON.parse(data.value);
          const collapsedObj = {};
          collapsedArr.forEach(id => { collapsedObj[id] = true; });
          setCollapsedStories(collapsedObj);
        } catch (e) {
          // fallback: do nothing
        }
      }
    }
    loadCollapsedStories();
  }, [supabase, user]);

  useEffect(() => {
    async function checkHideLetsGo() {
      if (!supabase || !user?.proj?.id || !user?.id) return;
      const { data } = await supabase
        .from('settings')
        .select('id')
        .eq('name', 'hideLetsGo')
        .eq('project_id', user.proj.id)
        .eq('user_id', user.id);
      if (data && data.length > 0) setHideLetsGo(true);
    }
    checkHideLetsGo();
  }, [supabase, user]);

  // Load view mode setting from cached user settings
  useEffect(() => {
    // Load viewMode from settings when user/project changes
    // But don't override if user is actively changing the view mode
    if (user?.proj?.settings && !userChangingViewMode.current) {
      // eslint-disable-next-line no-unused-vars
      const savedViewMode = user.proj.settings.find((s) => s.name === "workViewMode")?.value;
      // View mode loading removed - list view no longer supported
    }
  }, [user, viewMode]);

  const closeLetsGo = async () => {
    if (!supabase || !user?.proj?.id || !user?.id) return;
    await supabase.from('settings').upsert({
      name: 'hideLetsGo',
      value: '1',
      project_id: user.proj.id,
      user_id: user.id,
    });
    setHideLetsGo(true);
  };

  // Removed all list view functions: convertGoalsToWorkItems, buildTree, flattenTree, submitItemMove

  const addItemForKanban = useCallback(
    async () => {
      // Add story to unplanned goal in Kanban view
      const unplannedGoal = goals.find(g => g.type === 'unplanned');
      if (unplannedGoal && user?.proj?.id) {
        const { data: newWorkItem } = await supabase
          .from("work")
          .insert({ 
            title: "", 
            goal_id: unplannedGoal.id,
            project_id: user.proj.id,
            parent_id: null,
            type: 'story',
            status: null,
            child_order: []
          })
          .select()
          .single();

        if (newWorkItem) {
          const currentOrder = unplannedGoal.workitems_order || [];
          const newOrder = [...currentOrder, newWorkItem.id];
          
          await supabase.from("goals").update({ workitems_order: newOrder }).eq("id", unplannedGoal.id);
          
          // Update local goals state
          const updatedGoals = goals.map(g => 
            g.id === unplannedGoal.id 
              ? {
                  ...g,
                  workitems_order: newOrder,
                  stories: [...g.stories, { 
                    id: newWorkItem.id, 
                    title: "",
                    goal_id: unplannedGoal.id,
                    parent_id: null,
                    type: 'story',
                    children: [],
                    child_order: []
                  }]
                }
              : g
          );
          
          setGoals(updatedGoals);
          
          // Set editing state for the new story
          setEditingStory({ id: newWorkItem.id, goal: { id: unplannedGoal.id }, title: "" });
        }
      }
    },
    [goals, user?.proj?.id, supabase, setGoals, setEditingStory]
  );

  const addTaskToStory = useCallback(
    async (storyId, status) => {
      if (!user?.proj?.id) return;
      
      // Create new task with parent_id = null and story_id = storyId
      const { data: newWorkItem } = await supabase
        .from("work")
        .insert({ 
          title: "", 
          project_id: user.proj.id,
          parent_id: null, // Tasks created in Work page have no parent
          story_id: storyId, // Reference to the story
          type: 'task',
          status: status,
          child_order: []
        })
        .select()
        .single();

      if (newWorkItem) {
        // Update parent story's child_order
        const story = goals.find(g => g.stories?.some(s => s.id === storyId))?.stories?.find(s => s.id === storyId);
        if (story) {
          const currentChildOrder = story.child_order || [];
          const newChildOrder = [...currentChildOrder, newWorkItem.id];
          
          await supabase.from("work").update({ child_order: newChildOrder }).eq("id", storyId);
          
          // Update local goals state
          const updatedGoals = goals.map(g => ({
            ...g,
            stories: g.stories?.map(s => 
              s.id === storyId 
                ? { ...s, child_order: newChildOrder, children: [...(s.children || []), { 
                    id: newWorkItem.id, 
                    title: "",
                    parent_id: null,
                    story_id: storyId,
                    type: 'task',
                    status: status,
                    owner_id: null,
                    estimate: null
                  }] }
                : s
            )
          }));
          
          setGoals(updatedGoals);
          
          // Set inline editing state for the new task (just the id, no full task structure)
          setEditingTask({ id: newWorkItem.id });
        }
      }
    },
    [goals, user?.proj?.id, supabase, setGoals, setEditingTask]
  );

  // Removed addItem function (list view only)



  //----------------------------------------------------

  if (loading) {
    return <Loading />;
  }

  if (!hasProjectAccess) {
    return (
      <center style={{ height: "100%", marginTop: 100 }}>
        <h2>No active projects</h2>
        <br />
        <p>Let's create your own project to get building!</p>
      </center>
    );
  }

  return (
    <>
      <div style={{ marginTop: 12 }}>
        {/* Add Story Button and View Toggle - Top Right */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          gap: 12,
          paddingRight: 32
        }}>
          <FilterButton 
            text="Completed" 
            checked={showCompletedStories}
            onClick={toggleCompletedStories}
          />
          <Button 
            text="Add Story" 
            callback={() => {
              // Create story in Kanban view - add to unplanned goal for now
              addItemForKanban();
            }} 
            small
            image={plus}
            imgSize={16}
            noborder
          />
          {/* Temporarily hidden - Board/List view switch button */}
          {/* <FilterButton 
            text={viewMode === 'kanban' ? 'Board' : 'List'}
            checked={viewMode === 'kanban'}
            onClick={() => {
              const newMode = viewMode === 'kanban' ? 'list' : 'kanban';
              
              // Set flag to prevent useEffect from overriding
              userChangingViewMode.current = true;
              
              // Update state immediately
              setViewMode(newMode);
              
              // Save the setting
              saveViewModeSetting(newMode);
              
              // Reset flag after a short delay to allow useEffect to run again
              setTimeout(() => {
                userChangingViewMode.current = false;
              }, 100);
            }}
          /> */}
        </div>
        
        {viewMode === 'kanban' && (
          <FlexLeftCol>
            <DragDropContext onDragEnd={dragEnded}>
              {!loading &&
                goals &&
                goals.map((goal, k) => {
                const isUnplanned = goal.type === "unplanned";
                const isHidden = goalsHidden.includes(goal.id);
                const isCollapsed = collapsedGoals[goal.id];
                const color = goal.color ? goal.color : GoalStyles[goal.type]?.color || "transparent";
                const canCollapse = goal.workitems_order && goal.workitems_order.length > 1;
                return (
                  <Droppable key={k + "-"} droppableId={k + "-"} type="story">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} style={{ marginBottom: isCollapsed ? 0 : (isHidden ? 0 : 40) }}>
                        <div style={{
                          padding: isCollapsed ? '20px 32px 4px 32px' : (isUnplanned && (!goal.workitems_order || goal.workitems_order.length <= 1) ? '0px 32px 24px 32px' : '0px 32px 24px 32px'),
                          position: 'relative',
                          minHeight: isCollapsed ? undefined : (isUnplanned && (!goal.workitems_order || goal.workitems_order.length <= 1) ? 60 : 80),
                          transition: 'box-shadow 0.2s, min-height 0.2s, padding 0.2s',
                        }}>
                          {/* Absolutely positioned header, visually stretching out of frame to the left */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: '-40px', // stretch out of container
                            height: 0,
                            width: 'calc(100% + 40px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pointerEvents: 'none',
                            zIndex: 3,
                          }}>
                            <div style={{
                              position: 'relative',
                              top: '-50%',
                              pointerEvents: 'auto',
                              zIndex: 4,
                              display: 'flex',
                              alignItems: 'center',
                            }}>
                              {!isUnplanned && (
                                <Ribbon
                                  name={goal.name}
                                  type={"massive"}
                                  color={color}
                                  noBackground={false}
                                  isHidden={isHidden}
                                  onClick={canCollapse ? () => {
                                    setCollapsedGoals((prev) => {
                                      const updated = { ...prev, [goal.id]: !prev[goal.id] };
                                      persistCollapsedGoals(updated);
                                      return updated;
                                    });
                                  } : undefined}
                                  collapsed={canCollapse ? !!collapsedGoals[goal.id] : false}
                                  showChevron={canCollapse}
                                />
                              )}

                              {!isUnplanned && (
                                <>
                                  <div style={{ width: 8 }} />
                                  <ButtonTiny 
                                    text="Remove" 
                                    callback={() => removeGoalFromView(goal.id)} 
                                    light 
                                  />
                                </>
                              )}
                            </div>
                          </div>
                          {!isCollapsed && (
                            <div style={{ height: isHidden ? 0 : "calc-size(auto, size)", transition: "height 200ms ease" }}>
                              {(goal.workitems_order || []).map((story_id, i) => {
                                const story = goal.stories.find((stry) => stry.id === story_id);
                                if (!story) return null; // Skip if story not found
                                
                                // Skip completed stories if they're hidden
                                if (!showCompletedStories && story.status === "COMPLETE") {
                                  return null;
                                }
                                
                                // Calculate flat story index across all goals (counting only visible stories)
                                // This matches selectedStory which is now an index into flatStories (visible stories only)
                                let flatStoryIndex = 0;
                                for (let gIdx = 0; gIdx < k; gIdx++) {
                                  const prevGoal = goals[gIdx];
                                  const prevOrder = prevGoal.workitems_order || [];
                                  // Count only visible stories in previous goals
                                  for (let pIdx = 0; pIdx < prevOrder.length; pIdx++) {
                                    const prevStoryId = prevOrder[pIdx];
                                    const prevStory = prevGoal.stories.find((s) => s.id === prevStoryId);
                                    if (prevStory && (showCompletedStories || prevStory.status !== "COMPLETE")) {
                                      flatStoryIndex++;
                                    }
                                  }
                                }
                                // Count visible stories before current one in this goal
                                const currentOrder = goal.workitems_order || [];
                                for (let j = 0; j < i; j++) {
                                  const prevStoryId = currentOrder[j];
                                  const prevStory = goal.stories.find((s) => s.id === prevStoryId);
                                  if (prevStory && (showCompletedStories || prevStory.status !== "COMPLETE")) {
                                    flatStoryIndex++;
                                  }
                                }
                                
                                return (
                                  <FlexLeftColNoGap key={"keystory" + i}>
                                    <Story
                                      indexPath={[k, i]}
                                      goalId={goal.id}
                                      story={story}
                                      deleteStoryCb={deleteStory}
                                      completeStoryCb={completeStory}
                                      reactivateStoryCb={reactivateStory}
                                      index={flatStoryIndex}
                                      storyRef={(node) => {
                                        if (node) {
                                          storyRefs.current[flatStoryIndex] = node;
                                        } else {
                                          delete storyRefs.current[flatStoryIndex];
                                        }
                                      }}
                                      addTaskCb={(story_id, story_idx, col_id, col_idx) => {
                                        // Create a new task in the specified column
                                        addTaskToStory(story_id, col_id);
                                      }}
                                      onClickStoryCb={(goalId, storyId) => {
                                        const goal = goals.find((g) => g.id === goalId);
                                        const story = goal.stories.find((st) => st.id === storyId);
                                        
                                        // Calculate flat story index for selection
                                        let flatIdx = 0;
                                        const goalIdx = goals.findIndex((g) => g.id === goalId);
                                        for (let gIdx = 0; gIdx < goalIdx; gIdx++) {
                                          const prevGoal = goals[gIdx];
                                          flatIdx += (prevGoal.workitems_order || []).length;
                                        }
                                        const storyIdx = goal.workitems_order?.findIndex((id) => id === storyId) ?? 
                                                       goal.stories.findIndex((st) => st.id === storyId);
                                        if (storyIdx !== -1) {
                                          flatIdx += storyIdx;
                                        }
                                        
                                        setSelectedStory(flatIdx);
                                        setSelectedTask(-1);
                                        setEditingStory({ goal: { id: goal.id }, id: storyId, title: story.title });
                                      }}
                                      isEditing={editingStory?.id === story.id}
                                      onSaveTitle={async (newTitle) => {
                                        if (editingStory?.id) {
                                          // Check if this is a newly created story (original title was empty)
                                          const isNewStory = !editingStory.title || editingStory.title.trim() === "";
                                          
                                          if (newTitle === null || (!newTitle || !newTitle.trim())) {
                                            // If it's a new story with no title, delete it
                                            if (isNewStory) {
                                              // Find the story's goal and index path
                                              const goal = goals.find(g => g.stories?.some(s => s.id === editingStory.id));
                                              if (goal) {
                                                const goalIdx = goals.findIndex(g => g.id === goal.id);
                                                const storyIdx = goal.stories.findIndex(s => s.id === editingStory.id);
                                                const indexPath = [goalIdx, storyIdx];
                                                
                                                // Delete the story without confirmation
                                                await deleteStory(indexPath, editingStory.id, "", true);
                                              }
                                            } else {
                                              // Existing story - just cancel editing
                                              setEditingStory(false);
                                            }
                                          } else if (newTitle.trim()) {
                                            // Save the title
                                            await supabase
                                              .from("work")
                                              .update({ title: newTitle.trim() })
                                              .eq("id", editingStory.id);
                                            
                                            // Update local state
                                            const updatedGoals = goals.map(g => ({
                                              ...g,
                                              stories: g.stories.map(s => 
                                                s.id === editingStory.id 
                                                  ? { ...s, title: newTitle.trim() }
                                                  : s
                                              )
                                            }));
                                            setGoals(updatedGoals);
                                            setEditingStory(false);
                                          }
                                        }
                                      }}
                                      editTaskCb={(task, story, col, colIdx, isTitleClick = false) => {
                                        // Calculate flat story index for selection
                                        let flatStoryIdx = 0;
                                        const goalIdx = goals.findIndex((g) => g.id === goal.id);
                                        for (let gIdx = 0; gIdx < goalIdx; gIdx++) {
                                          const prevGoal = goals[gIdx];
                                          flatStoryIdx += (prevGoal.workitems_order || []).length;
                                        }
                                        const storyIdx = goal.workitems_order?.findIndex((id) => id === story.id) ?? 
                                                       goal.stories.findIndex((st) => st.id === story.id);
                                        if (storyIdx !== -1) {
                                          flatStoryIdx += storyIdx;
                                        }
                                        
                                        // Calculate task index across all columns in the story
                                        let taskIndex = 0;
                                        // Use the story object passed in, which should match the actual story
                                        const currentStoryObj = goal.stories.find(s => s.id === story.id);
                                        if (currentStoryObj && currentStoryObj.children) {
                                          // Calculate offset from previous columns
                                          for (let cIdx = 0; cIdx < colIdx; cIdx++) {
                                            const prevCol = columns[cIdx];
                                            const prevColTasks = currentStoryObj.children.filter(t => t.status === prevCol.id);
                                            taskIndex += prevColTasks.length;
                                          }
                                          // Find task index within its column
                                          const colTasks = currentStoryObj.children.filter(t => t.status === col.id);
                                          const taskIdxInCol = colTasks.findIndex(t => t.id === task.id);
                                          if (taskIdxInCol !== -1) {
                                            taskIndex += taskIdxInCol;
                                          }
                                        }
                                        
                                        setSelectedStory(flatStoryIdx);
                                        setSelectedTask(taskIndex);
                                        
                                        if (isTitleClick) {
                                          // Set inline editing state for task title
                                          setEditingTask({ id: task.id });
                                        } else {
                                          // Set modal editing state for task (with full structure for EditTask component)
                                        setEditingTask({
                                          goal: { id: goal.id },
                                          story: { id: story.id },
                                          col: { id: col.id, index: colIdx },
                                          task: {
                                            id: task.id,
                                            title: task.title,
                                              owner_id: task.owner_id,
                                            description: task.description,
                                            parent_id: task.parent_id,
                                            estimate: task.estimate,
                                            history: task.history,
                                          },
                                        });
                                        }
                                      }}
                                      editingTaskId={editingTask?.id && !editingTask.task && !editingTask.col ? editingTask.id : null}
                                      onSaveTaskTitle={async (newTitle) => {
                                        if (editingTask?.id && !editingTask.task && !editingTask.col) {
                                          // Inline editing for task title (new or existing)
                                          if (newTitle === null) {
                                            // Cancel editing - check if task is newly created (empty title)
                                            const task = goals
                                              .flatMap(g => g.stories || [])
                                              .flatMap(s => s.children || [])
                                              .find(t => t.id === editingTask.id);
                                            
                                            // If task has empty title, it's newly created - delete it
                                            if (task && (!task.title || task.title.trim() === "")) {
                                              // Find the story containing this task
                                              const story = goals
                                                .flatMap(g => g.stories || [])
                                                .find(s => s.children?.some(t => t.id === editingTask.id));
                                              
                                              if (story) {
                                                // Update parent story's child_order to remove this task
                                                const currentChildOrder = story.child_order || [];
                                                const newChildOrder = currentChildOrder.filter(taskId => taskId !== editingTask.id);
                                                
                                                // Update story's child_order in database
                                                await supabase
                                                  .from("work")
                                                  .update({ child_order: newChildOrder.length > 0 ? newChildOrder : null })
                                                  .eq("id", story.id);
                                                
                                                // Delete the task from database
                                                await supabase
                                                  .from("work")
                                                  .delete()
                                                  .eq("id", editingTask.id);
                                                
                                                // Update local state
                                                const updatedGoals = goals.map(g => ({
                                                  ...g,
                                                  stories: g.stories.map(s => 
                                                    s.id === story.id
                                                      ? {
                                                          ...s,
                                                          child_order: newChildOrder,
                                                          children: s.children.filter(t => t.id !== editingTask.id)
                                                        }
                                                      : s
                                                  )
                                                }));
                                                setGoals(updatedGoals);
                                              }
                                            }
                                            setEditingTask(null);
                                          } else if (newTitle !== null && newTitle.trim()) {
                                            // Save the title
                                            const { error } = await supabase
                                              .from("work")
                                              .update({ title: newTitle.trim() })
                                              .eq("id", editingTask.id);
                                            
                                            if (!error) {
                                              // Update local state
                                              const updatedGoals = goals.map(g => ({
                                                ...g,
                                                stories: g.stories.map(s => ({
                                                  ...s,
                                                  children: s.children.map(t => 
                                                    t.id === editingTask.id 
                                                      ? { ...t, title: newTitle.trim() }
                                                      : t
                                                  )
                                                }))
                                              }));
                                              setGoals(updatedGoals);
                                            }
                                            // Don't setEditingTask(null) here - keep editing mode active for Tab navigation
                                          } else if (newTitle !== null && !newTitle.trim()) {
                                            // Empty title on blur - check if task is newly created
                                            const task = goals
                                              .flatMap(g => g.stories || [])
                                              .flatMap(s => s.children || [])
                                              .find(t => t.id === editingTask.id);
                                            
                                            // If task has empty title, it's newly created - delete it
                                            if (task && (!task.title || task.title.trim() === "")) {
                                              // Find the story containing this task
                                              const story = goals
                                                .flatMap(g => g.stories || [])
                                                .find(s => s.children?.some(t => t.id === editingTask.id));
                                              
                                              if (story) {
                                                // Update parent story's child_order to remove this task
                                                const currentChildOrder = story.child_order || [];
                                                const newChildOrder = currentChildOrder.filter(taskId => taskId !== editingTask.id);
                                                
                                                // Update story's child_order in database
                                                await supabase
                                                  .from("work")
                                                  .update({ child_order: newChildOrder.length > 0 ? newChildOrder : null })
                                                  .eq("id", story.id);
                                                
                                                // Delete the task from database
                                                await supabase
                                                  .from("work")
                                                  .delete()
                                                  .eq("id", editingTask.id);
                                                
                                                // Update local state
                                                const updatedGoals = goals.map(g => ({
                                                  ...g,
                                                  stories: g.stories.map(s => 
                                                    s.id === story.id
                                                      ? {
                                                          ...s,
                                                          child_order: newChildOrder,
                                                          children: s.children.filter(t => t.id !== editingTask.id)
                                                        }
                                                      : s
                                                  )
                                                }));
                                                setGoals(updatedGoals);
                                              }
                                            }
                                            setEditingTask(null);
                                          }
                                        }
                                      }}
                                      onSaveTaskEstimate={async (estimate) => {
                                        if (editingTask?.id && !editingTask.task && !editingTask.col) {
                                          // Save the estimate
                                          await supabase
                                            .from("work")
                                            .update({ estimate: estimate })
                                            .eq("id", editingTask.id);
                                          
                                          // Update local state
                                          const updatedGoals = goals.map(g => ({
                                            ...g,
                                            stories: g.stories.map(s => ({
                                              ...s,
                                              children: s.children.map(t => 
                                                t.id === editingTask.id 
                                                  ? { ...t, estimate: estimate }
                                                  : t
                                              )
                                            }))
                                          }));
                                          setGoals(updatedGoals);
                                        }
                                      }}
                                      onSaveTaskAssignee={async (ownerId) => {
                                        if (editingTask?.id && !editingTask.task && !editingTask.col) {
                                          // Get the current task to check if title needs saving
                                          const task = goals
                                            .flatMap(g => g.stories || [])
                                            .flatMap(s => s.children || [])
                                            .find(t => t.id === editingTask.id);
                                          
                                          // Save the assignee (owner_id)
                                          await supabase
                                            .from("work")
                                            .update({ owner_id: ownerId })
                                            .eq("id", editingTask.id);
                                          
                                          // If title exists and hasn't been saved yet, save it
                                          if (task && task.title && task.title.trim()) {
                                            await supabase
                                              .from("work")
                                              .update({ title: task.title.trim() })
                                              .eq("id", editingTask.id);
                                          }
                                          
                                          // Update local state
                                          const updatedGoals = goals.map(g => ({
                                            ...g,
                                            stories: g.stories.map(s => ({
                                              ...s,
                                              children: s.children.map(t => 
                                                t.id === editingTask.id 
                                                  ? { ...t, owner_id: ownerId }
                                                  : t
                                              )
                                            }))
                                          }));
                                          setGoals(updatedGoals);
                                          
                                          // End inline editing after saving assignee
                                          setEditingTask(null);
                                        }
                                      }}
                                      users={users.current}
                                      usersForDisplay={usersForDisplay}
                                      selectedStory={selectedStory}
                                      selectedTask={selectedTask}
                                      totalStories={(goal.workitems_order || []).length}
                                      collapsed={collapsedStories[story.id]}
                                      onToggleCollapse={(storyId) => {
                                        setCollapsedStories((prev) => {
                                          const updated = { ...prev, [storyId]: !prev[storyId] };
                                          persistCollapsedStories(updated);
                                          return updated;
                                        });
                                      }}
                                      columns={columns}
                                    />
                                  </FlexLeftColNoGap>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </DragDropContext>
          </FlexLeftCol>
        )}

      </div>

      {!loading && viewMode === 'kanban' && goals.length === 1 && goals[0].type === "unplanned" && !goals[0].workitems_order && (!goals[0].stories || goals[0].stories.length === 0) && !hideLetsGo && (
        <div style={{ position: 'relative', width: '100%', height: 120, marginTop: -50 }}>
          <div style={{ position: 'absolute', right: 240, top: 0, display: 'flex', alignItems: 'end' }}>
            <img
              alt="arrow"
              src={arrow}
              style={{
                position: 'absolute',
                right: '-160px',
                top: -40,
                width: 130,
                transform: 'rotate(-10deg)',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative', display: 'inline-block', zIndex: 2, textAlign: 'right' }}>
              <h2 style={{ display: 'inline-block', marginRight: 8 }}>Let's go!</h2>
              <FaTimes onClick={closeLetsGo} style={{ position: 'absolute', top: 2, right: -22, fontSize: 16, cursor: 'pointer', color: '#888', zIndex: 10 }} />
              <div style={{ fontSize: 16, marginTop: 2, color: '#f9f9ff', fontWeight: 400 }}>What do you want to achieve first?</div>
            </div>
          </div>
        </div>
      )}

      {/* Modal editing for Shift+Cmd/Ctrl+Enter */}
      {editingStoryModal && (
        <Modal closeCb={() => {
          setEditingStoryModal(null);
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 320 }}>
            <h2 style={{ color: "#000", marginTop: -11 }}>{editingStoryModal?.title ? "Edit Story" : "New Story"}</h2>
            <Input 
              ref={newTitle} 
              placeholder="Story title..." 
              onChange={() => validateText()} 
              autoFocus 
              left 
              value={editingStoryModal?.title || ""} 
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitStory();
                } else if (e.key === 'Escape') {
                  setEditingStoryModal(null);
                }
              }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 8, justifyContent: 'flex-end' }}>
              <Button text="Save" callback={() => submitStory()} large color="purple" />
              <Button text="Cancel" callback={() => setEditingStoryModal(null)} />
            </div>
          </div>
        </Modal>
      )}

      {editingTask && editingTask.task && editingTask.task.id > -1 && editingTask.story && (
        <Modal closeCb={() => {
          setEditingTask(null);
        }}>
          <EditTask 
            submitTask={submitTask} 
            editingTask={editingTask} 
            users={users.current} 
            usersForDisplay={usersForDisplay}
            deleteTask={(id) => deleteTask(id)}
            subEpics={subEpics}
            onSubEpicSelect={handleSubEpicSelect}
            supabase={supabase}
            user={user}
          />
        </Modal>
      )}

        <Search
          isVisible={searchVisible}
          onClose={() => {
            setSearchVisible(false);
            setSearchText("");
            setHighlightedItem(null);
            typingBufferRef.current = "";
          }}
          onSearch={handleSearch}
          onTextChange={setSearchText}
          placeholder="Search stories and tasks..."
          initialText={searchText}
        />

    </>
  );
}

const Ribbon = ({ color, type, name, opacity, noBackground, isHidden, onClick, collapsed, showChevron }) => {
  return (
    <div
      style={{
        display: "inline-flex",
        background: noBackground || isHidden ? "transparent" : color ? color : GoalStyles[type].color,
        cursor: "pointer",
        boxShadow: collapsed || noBackground || isHidden ? "none" : "#00000055 1px 4px 5px 0px",
        opacity: isHidden ? 0.6 : opacity,
        minWidth: 180,
        border: isHidden ? "1px solid " + (color ? color : GoalStyles[type].color) : "none",
        transform: "skew(-10deg)",
        alignItems: "center",
      }}
      onClick={onClick}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "nowrap",
          width: "100%",
          whiteSpace: "nowrap",
          alignItems: "center",
          justifyContent: "center",
          textShadow: "rgba(0, 0, 0, 0.7) 1px 1px 0px",
          fontSize: GoalStyles[type].fontSize + 4,
          height: GoalStyles[type].height,
          padding: `4px ${GoalStyles[type].padding + 40}px`,
          fontWeight: "bold",
          userSelect: "none",
          color: isHidden && !noBackground ? (color ? color : GoalStyles[type].color) : "white",
          transform: "skew(10deg)",
        }}
      >
        {showChevron && (
          <span style={{ marginRight: 10, display: "inline-flex", alignItems: "center", fontSize: '0.9em', lineHeight: 1 }}>
            {collapsed ? <FaChevronRight /> : <FaChevronDown />}
          </span>
        )}
        {name ? name : "unnamed"}
      </div>
    </div>
  );
};

